/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EEComponents.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 24-07-2024
 * @description : Component to add, update and replace ee component.
 */
import CloseIcon from "@mui/icons-material/Close";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";

import TextField from "@mui/material/TextField";

import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useRef, useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import VlcSWModules from "./SWModules";
import {
  adminSettingsAPI,
  HandleApiActions,
  labCar,
} from "../../redux/actions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import customConsole from "../../config/customConsole";
import { useLocation, useNavigate } from "react-router-dom";
import editRequestAPI from "../../redux/actions/editRequestApi";

import LockIcon from "@mui/icons-material/Lock";
import NoEncryptionGmailerrorredIcon from "@mui/icons-material/NoEncryptionGmailerrorred";
import moment from "moment";
import actionType from "../../redux/actionTypes";
import { AppBar, InputAdornment, Toolbar } from "@mui/material";
import typeApprovalAPI from "../../redux/actions/typeApprovalAPI";
import ruleEngineActions from "../../redux/actions/ruleEngineAction";
import { componentStyle } from "../../config/appTheme";
import {
  vlcCardStyles,
  vlcComponentStyle,
} from "../../components/customComponentsV2/vlc/config/vlcConfig";
import { Search } from "@mui/icons-material";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomPaginationV2 from "../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import VlcCardHeader from "../../components/customComponentsV2/vlc/cards/VlcCardHeader";
import VlcCardContent from "../../components/customComponentsV2/vlc/cards/VlcCardContent";
import EditDetailsDialog from "../../components/customComponentsV2/vlc/pages/EditDetailsDialog";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../../components/customComponentsV2/common/connectors/MultiselectConnectorV2";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";
import DetailsLogTab from "../../components/customComponentsV2/vlc/pages/DetailsLogTab";
import CustomToolTip from "../../components/infoButton/CustomToolTip";
import VlcEEComponentAddDialog from "./VlcEEComponentAddDialog";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import RequestsAndLogsTabs from "./requestAccess";

import RecreateEEDialog from "../../components/customComponentsV2/vlc/pages/RecreateEEDialog";
import DecisionMatrixPanel from "../legal-requirements/RuleCenter/Executions/DecisionMatrixPanel";
import { showToastMessage } from "../../redux/actions/actionHandler";
import PropTypes from "prop-types";
import AttributesForm from "./Attributes/AttributesForm";
import AttributeUpdateDialog from "./Attributes/AttributeUpdateDialog";
import SelectVehicleComponentForVehicleSystem from "./SelectVehicleComponentForVehicleSystem";

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 24-07-2024
 * @description : Show the ee component component
 * @param : heading, eEComponentsList, handleEEComponentAdd, handleEEComponentUpdate, handleEEComponentReplace, selectedProjectDetails, selectedZone,
 * @return : The rendered ee component.
 */
function VlcEEComponents({
  getZoneEECountList,
  variantOptionList,
  heading,
  selectedSingleVariantDetails,
  eEComponentsList,
  handleEEComponentAdd,
  handleEEComponentUpdate,
  handleEEComponentReplace,
  selectedProjectDetails,
  selectedZone,
  selectedZoneDetails,
  setIsLinkSoftwareModulesScreen,
  getEEComponentsList,
  searchFieldValue,
  setSearchFieldValue,
  selectedSearchField,
  setSelectedSearchField,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  hasMore,
  setHasMore,
  swmPage,
  setSwmPage,
  swmRowsPerPage,
  setSwmRowsPerPage,
  swmHasMore,
  setSwmHasMore,
  swmNameFilter,
  setSWMNameFilter,
  swmVersionFilter,
  setSWMVersionFilter,
  swmNameFilterSearch,
  setSWMNameFilterSearch,
  swmVersionFilterSearch,
  setSWMVersionFilterSearch,
  swmSortField,
  setSWMSortField,
  swmSortOrder,
  setSWMSortOrder,
  swmListToFilter,
  filteredSWMList,
  sortedSWMList,
  selectedEEComponentDetails,
  setSelectedEEComponentDetails,
  totalEECount,
  setTotalEECount,
  totalSWMCount,
  setTotalSWMCount,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedEEIdFromUrl = queryParams.get("ee_id");
  const selectedSWMIdFromUrl = queryParams.get("swm_id");
  const actionModeFromUrl = queryParams.get("actionMode");
  const isRedirectedEEAction =
    actionModeFromUrl === "eeComponentAction" && Boolean(selectedEEIdFromUrl);
  const isRedirectedSWMAction =
    actionModeFromUrl === "swmAction" && Boolean(selectedSWMIdFromUrl);

  const { swModulesList } = useSelector(
    (state) => state.labCarReducer,
    shallowEqual,
  );
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
    shallowEqual,
  );

  const profileDetails = useSelector((store) => store.profileReducer);

  // Extract role_type from profileDetails
  const roleType = profileDetails?.profile?.role_details?.role_type;

  const supplier = roleType === 2;

  const [activeItemId, setActiveItemId] = useState(null); // State to track which item's actions are visible
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  /* -------------- Dialogs -------------- */
  const [openEEComponentAddDialog, setOpenEEComponentAddDialog] =
    useState(false);
  const [openEEComponentUpdateDialog, setOpenEEComponentUpdateDialog] =
    useState(false);
  const [openEEComponentReplaceDialog, setOpenEEComponentReplaceDialog] =
    useState(false);
  const [openEEComponentChangeLogsDialog, setOpenEEComponentChangeLogsDialog] =
    useState(false);
  const [openRequestsDialog, setOpenRequestsDialog] = useState(false);
  const [openAttributeUpdateDialog, setOpenAttributeUpdateDialog] =
    useState(false);

  // Ref for AttributeUpdateDialog to call handleContinueAfterVersionUpdate
  const attributeUpdateDialogRef = useRef(null);

  // State to track if we're in the version impact flow
  const [isVersionImpactFlow, setIsVersionImpactFlow] = useState(false);
  const [
    resumeAttributeImpactFlowTrigger,
    setResumeAttributeImpactFlowTrigger,
  ] = useState(0);
  const [resumeVersionImpactFlowTrigger, setResumeVersionImpactFlowTrigger] =
    useState(0);

  const [openRecreationEEDialog, setOpenRecreationEEDialog] = useState(false);
  const [recreationParentEEList, setRecreationParentEEList] = useState([]);
  const [selectedParentEEDetails, setSelectedParentEEDetails] = useState(null);

  /* -------------- Selected ee component -------------- */
  const [isEEComponentSelected, setIsEEComponentSelected] = useState(false);

  /* -------------- Selected ee component update/replace details  -------------- */
  const [
    selectedEEComponentUpdateDetails,
    setSelectedEEComponentUpdateDetails,
  ] = useState(null);
  const [
    selectedEEComponentReplaceDetails,
    setSelectedEEComponentReplaceDetails,
  ] = useState(null);

  /* -------------- EE - SW Modules -------------- */
  const [eESWModulesList, setEESWModulesList] = useState([]);

  /* -------------- Vehicle Systems-------------- */

  const [vsOptionsList, setVSOptionsList] = useState([]);
  const [supplierOptionsList, setSupplierOptionsList] = useState([]);

  /* -------------- EE Change Logs -------------- */
  const [eeChangeLogsList, setEEChangeLogsList] = useState([]);
  const [eeChangeLogsTotalCount, setEEChangeLogsTotalCount] = useState(0);
  const [eeLogsDialogData, setEELogsDialogData] = useState({});

  /* -------------- Pending Requests -------------- */

  const [anchorEl, setAnchorEl] = useState(null);

  const [getRequestsData, setGetRequestData] = useState([]);

  const [selectedECU, setSelectedECU] = useState(null);
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);
  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);

  // Dialog states
  const [openDialogDraftToAdd, setOpenDialogDraftToAdd] = useState(false);
  const [openLockUnLockEEDialog, setOpenLockUnLockEEDialog] = useState(null);
  const [lockUnLockEEReason, setLockUnLockEEReason] = useState(null);
  const [lockUnLockEEData, setLockUnLockEEData] = useState(null);
  const [openActionButton, setOpenActionButton] = useState(false);
  // Filtering
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [filteredEECompSupplierName, setFilteredEECompSupplierName] = useState(
    [],
  );
  const [filteredEECompAssemblyNum, setFilteredEECompAssemblyNum] = useState(
    [],
  );
  const [filteredEECompVersion, setFilteredEECompVersion] = useState([]);
  const [swmSelectedSearchField, setSWMSelectedSearchField] = useState("name");
  const [swmSearchFieldValue, setSWMSearchFieldValue] = useState("");

  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);
  const [openEditDetailsDialog, setOpenEditDetailsDialog] = useState(false);
  const [selectedEEItem, setSelectedEEItem] = useState(null);
  const [attributeDecisionMatrixContext, setAttributeDecisionMatrixContext] =
    useState(null);
  const [showAttributeDecisionMatrix, setShowAttributeDecisionMatrix] =
    useState(false);
  const [attributeExecutionId, setAttributeExecutionId] = useState(null);
  const [attributeSimilarTickets, setAttributeSimilarTickets] = useState([]);
  const [attributeSelectedEntity, setAttributeSelectedEntity] = useState(null);
  const [attributeSelectedTicket, setAttributeSelectedTicket] = useState(null);
  const [attributeOpenAiPanel, setAttributeOpenAiPanel] = useState(false);
  const [attributeCreateTicketData, setAttributeCreateTicketData] = useState(
    {},
  );
  const [attributeDecisionMatrixReason, setAttributeDecisionMatrixReason] =
    useState("");
  const [attributeDisabledStandards, setAttributeDisabledStandards] = useState(
    [],
  );

  useEffect(() => {
    if (adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.length > 0) {
      const supplierDetails =
        adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.map(
          (data) => ({
            value: data.name,
            label: data.name,
          }),
        );
      setSupplierOptionsList(supplierDetails);
    }
  }, [adminSettingsDetails]);
  useEffect(() => {
    dispatch(HandleApiActions({ ...adminSettingsAPI.get_admin_settings }));
  }, []);

  // Build EE filter options from the current list (to avoid duplicate API calls)
  useEffect(() => {
    const list = Array.isArray(eEComponentsList) ? eEComponentsList : [];
    if (!list?.length) {
      setFilteredEECompSupplierName([]);
      setFilteredEECompAssemblyNum([]);
      setFilteredEECompVersion([]);
      return;
    }

    const supplierName = list.reduce((acc, component) => {
      if (
        component?.supplier_name &&
        !acc.find((i) => i.value === component.supplier_name)
      ) {
        acc.push({
          value: component.supplier_name,
          label: component.supplier_name,
        });
      }
      return acc;
    }, []);

    const assemblyNum = list
      ?.map((c) => c?.assembly_number)
      ?.filter(Boolean)
      ?.map((v) => ({ value: v, label: v }));

    const version = list
      ?.map((c) => c?.version)
      ?.filter(Boolean)
      ?.map((v) => ({ value: v, label: v }));

    setFilteredEECompAssemblyNum(assemblyNum);
    setFilteredEECompVersion(version);
    setFilteredEECompSupplierName(supplierName);
  }, [eEComponentsList]);

  // EE list fetch is handled in parent (VlcMain) to prevent duplicate calls

  useEffect(() => {
    handleFilterResetAll();
    // handleSortResetAll();
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  // Reset the filter values
  const handleFilterResetAll = () => {
    handleFilterChange("supplier_name");
  };

  // Handle filter change
  const handleFilterChange = (searchField, searchValue) => {
    setSelectedSearchField(searchField);
    setSearchFieldValue(searchValue);
    setPage(1);
    handleFilterClose();
  };

  // Function to handle filter
  const handleFilterApplyNow = (searchField, searchValue) => {
    handleFilterChange(searchField, searchValue);
  };

  // Open filter menu
  const handleFilterClick = (event) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilter(true);
  };

  // Handle filter close
  const handleFilterClose = () => {
    setAnchorElFilter(null);
    setOpenFilter(false);
  };

  // Handle page change
  const handlePageChange = (selectedPage) => {
    setPage(selectedPage);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  /* Handle lock/un-lock ee component with reason dialog */
  const handleLockUnLockEE = (params) => {
    setLockUnLockEEData(params);

    setOpenLockUnLockEEDialog(true);
  };
  const handleConfirmLockUnLockEE = async () => {
    let apiDetails = labCar.lock_unlock_ee_component;

    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails &&
      selectedSingleVariantDetails?.variant_id
    ) {
      customConsole({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(lockUnLockEEData?.eeId),
          is_locked: lockUnLockEEData?.isLocked,
          ...(lockUnLockEEReason && { reason: lockUnLockEEReason }),
        },
        show_toast: true,
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(lockUnLockEEData?.eeId),
            is_locked: lockUnLockEEData?.isLocked,
            ...(lockUnLockEEReason && { reason: lockUnLockEEReason }),
          },
          show_toast: true,
        }),
      );
      if (resp.error) {
      } else {
        getEEComponentsList();
        handleCloseLockUnLockEE();
      }
    }
  };
  const handleCloseLockUnLockEE = () => {
    setOpenLockUnLockEEDialog(false);

    // Delay clearing the reason by 300 milliseconds
    setTimeout(() => {
      setLockUnLockEEData(null);
      setLockUnLockEEReason("");
    }, 300);
  };

  // Functions to handle data //
  //
  const getVehicleSystemsDetails = async () => {
    let apiDetails = adminSettingsAPI.get_vehicle_systems;

    customConsole({
      ...apiDetails,
    });

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
      }),
    );

    if (resp.error) {
      setVSOptionsList([]);
    } else {
      setVSOptionsList(resp.data);
    }
  };

  /* -------------- useEffects -------------- */
  useEffect(() => {
    // Get Vehicle Systems Details
    getVehicleSystemsDetails();
  }, []);

  useEffect(() => {
    // Keep selected EE in sync with the latest refreshed list data.
    // This avoids stale selected object fields (e.g., status) after updates.
    if (Array.isArray(eEComponentsList) && eEComponentsList?.length > 0) {
      const selectedEEFromUrl = selectedEEIdFromUrl
        ? eEComponentsList.find(
          (ee) => Number(ee.ee_id) === Number(selectedEEIdFromUrl),
        )
        : null;
      const matchedSelectedEE = selectedEEComponentDetails
        ? eEComponentsList.find(
          (ee) => ee.ee_id === selectedEEComponentDetails.ee_id,
        )
        : null;

      if (selectedEEFromUrl) {
        setSelectedEEComponentDetails(selectedEEFromUrl);
      } else if (matchedSelectedEE) {
        setSelectedEEComponentDetails(matchedSelectedEE);
      } else {
        setSelectedEEComponentDetails(eEComponentsList[0]);
      }
    } else {
      if (selectedEEComponentDetails) setSelectedEEComponentDetails(null);
    }
    // eslint-disable-next-line
  }, [eEComponentsList, selectedEEIdFromUrl]);
  useEffect(() => {

  }, [eEComponentsList]);

  useEffect(() => {
    /* Make to default state when the ee component is changed */
    setEESWModulesList([]);
    setSwmPage(1);
    lastSwmFetchKeyRef.current = "";
  }, [selectedEEComponentDetails]);

  const getSWModulesList = async ({ force = false } = {}) => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const eeId = selectedEEComponentDetails?.ee_id;
    const redirectedActionSWMId =
      isRedirectedSWMAction &&
        String(selectedEEComponentDetails?.ee_id) === String(selectedEEIdFromUrl)
        ? selectedSWMIdFromUrl
        : null;
    const searchKey = `${swmSelectedSearchField}|${swmSearchFieldValue || ""}`;
    const reqKey = [
      projectId,
      variantId,
      eeId,
      redirectedActionSWMId,
      actionModeFromUrl,
      searchKey,
      swmPage,
      swmRowsPerPage,
    ].join("|");
    if (
      !force &&
      !redirectedActionSWMId &&
      lastSwmFetchKeyRef.current === reqKey
    )
      return;
    lastSwmFetchKeyRef.current = reqKey;
    if (projectId && variantId && eeId) {
      let resp = await dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: projectId,
            variant_id: Number(variantId),
            ee_id: eeId,
            ...(redirectedActionSWMId && {
              swm_id: Number(redirectedActionSWMId),
            }),
            ...(swmSearchFieldValue && {
              [swmSelectedSearchField]: swmSearchFieldValue,
            }),
            page_number: swmPage,
            page_size: swmRowsPerPage,
          },
          show_toast: false,
        }),
      );
      setTotalSWMCount(resp?.total_count);
      if (!resp?.error && resp?.data?.length >= swmRowsPerPage) {
        setSwmHasMore(true);
      } else {
        setSwmHasMore(false);
      }

      const redirectedSWMActionCompleted =
        redirectedActionSWMId &&
        resp?.data?.length === 1 &&
        ![4, 5].includes(Number(resp.data[0]?.action_type));

      if (redirectedSWMActionCompleted) {
        const paramsToClear = new URLSearchParams(location.search);
        paramsToClear.delete("ee_id");
        paramsToClear.delete("swm_id");
        paramsToClear.delete("actionMode");
        paramsToClear.delete("page");
        setSelectedEEComponentDetails(null);
        setEESWModulesList([]);
        lastSwmFetchKeyRef.current = "";
        navigate(`${location.pathname}?${paramsToClear.toString()}`, {
          replace: true,
        });
      }
    }
  };

  const lastSwmFetchKeyRef = useRef("");
  useEffect(() => {
    getSWModulesList();
    // eslint-disable-next-line
  }, [
    selectedEEComponentDetails,
    swmSelectedSearchField,
    swmSearchFieldValue,
    swmPage,
    swmRowsPerPage,
  ]);

  useEffect(() => {
    if (swModulesList) {
      setEESWModulesList(swModulesList);
    }
  }, [swModulesList]);

  useEffect(() => {
    customConsole(
      "selectedEEComponentUpdateDetails:" + selectedEEComponentUpdateDetails,
    );
    customConsole(
      "selectedEEComponentReplaceDetails:" + selectedEEComponentReplaceDetails,
    );
  }, [selectedEEComponentUpdateDetails, selectedEEComponentReplaceDetails]);

  /* -------------- Handle EE Component functions -------------- */
  const handleSelectedEEComponent = (eEComponent) => {
    if (eEComponent) {
      setIsEEComponentSelected(true);
      setSelectedEEComponentDetails(eEComponent);
    }
  };

  const handleSelectedEEComponentUpdateDetails = (eEComponent) => {
    setOpenEEComponentUpdateDialog(true);

    if (eEComponent) {
      setSelectedEEComponentUpdateDetails(eEComponent);
    }
  };

  const handleSelectedEEComponentReplaceDetails = (eEComponent) => {
    setOpenEEComponentReplaceDialog(true);

    if (eEComponent) {
      setSelectedEEComponentReplaceDetails(eEComponent);
    }
  };

  const clearRedirectedEEActionParams = () => {
    if (!isRedirectedEEAction) return;

    const paramsToClear = new URLSearchParams(location.search);
    paramsToClear.delete("ee_id");
    paramsToClear.delete("swm_id");
    paramsToClear.delete("actionMode");
    paramsToClear.delete("page");
    setSelectedEEComponentUpdateDetails(null);
    setOpenAttributeUpdateDialog(false);
    setOpenEEComponentUpdateDialog(false);
    navigate(`${location.pathname}?${paramsToClear.toString()}`, {
      replace: true,
    });
  };

  const clearRedirectedSWMActionParams = () => {
    if (!isRedirectedSWMAction) return;

    const paramsToClear = new URLSearchParams(location.search);
    paramsToClear.delete("ee_id");
    paramsToClear.delete("swm_id");
    paramsToClear.delete("actionMode");
    paramsToClear.delete("page");
    setSelectedEEComponentDetails(null);
    setEESWModulesList([]);
    lastSwmFetchKeyRef.current = "";
    navigate(`${location.pathname}?${paramsToClear.toString()}`, {
      replace: true,
    });
  };

  const isAttributeDecisionMatrixContextValid = (data) => {
    if (!data?.standards || data.standards.length === 0) {
      return false;
    }

    return data.standards.some((standard) => {
      if (!standard.rule_sets || standard.rule_sets.length === 0) {
        return false;
      }

      return standard.rule_sets.some(
        (ruleSet) => ruleSet.workflows && ruleSet.workflows.length > 0,
      );
    });
  };

  const loadAttributeDecisionMatrixContext = async () => {
    const targetEE =
      selectedEEComponentUpdateDetails || selectedEEComponentDetails;

    if (
      !targetEE?.ee_id ||
      !selectedProjectDetails?.project_id ||
      !selectedSingleVariantDetails?.variant_id
    ) {
      return;
    }

    const standardsRes = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.decision_matrix_get_context,
        params: {
          component_type: "EE",
          component_id: targetEE.ee_id,
          project_id: selectedProjectDetails.project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
        },
        show_toast: false,
      }),
    );

    const contextData = standardsRes?.data;

    if (!isAttributeDecisionMatrixContextValid(contextData)) {
      setAttributeDecisionMatrixContext(null);
      setShowAttributeDecisionMatrix(false);
      return;
    }

    setAttributeDecisionMatrixContext(contextData);
    setAttributeDecisionMatrixReason(reason?.trim() || "");
    setShowAttributeDecisionMatrix(true);
  };

  const handleAttributeDecisionMatrixClose = () => {
    setShowAttributeDecisionMatrix(false);
    setAttributeDecisionMatrixContext(null);
    setAttributeExecutionId(null);
    setAttributeSimilarTickets([]);
    setAttributeSelectedEntity(null);
    setAttributeSelectedTicket(null);
    setAttributeOpenAiPanel(false);
    setAttributeCreateTicketData({});
    setAttributeDecisionMatrixReason("");
    setAttributeDisabledStandards([]);
  };

  const handleAttributeExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_start,
          params: {
            workflowId: payload.workflowId,
            version: payload.version,
          },
          show_toast: false,
        }),
      );

      setAttributeExecutionId(res.executionId);
      return res;
    }

    if (mode === "COMPLETE") {
      return dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_node_complete,
          params: {
            executionId: payload.executionId,
            nodeId: payload.nodeId,
            input: payload.input,
          },
          show_toast: false,
        }),
      );
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleAttributeViewExecution = (executionId) => {
  };

  const handleAttributeDecisionMatrixAction = async ({ standardCode } = {}) => {
    const res = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.re_create_ticket_v1,
        params: {
          details: attributeCreateTicketData,
          ...(attributeDecisionMatrixReason && {
            reason: attributeDecisionMatrixReason,
          }),
        },
        show_toast: true,
      }),
    );

    if (!res?.error && standardCode) {
      setAttributeDisabledStandards((prev) => [
        ...new Set([...prev, standardCode]),
      ]);
    }

    return res;
  };

  const handelGetAttributeSimilerTickets = async ({ standard_name } = {}) => {
    const targetEE =
      selectedEEComponentUpdateDetails || selectedEEComponentDetails;

    const res = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.get_similar_tickets,
        params: {
          execution_id: attributeExecutionId,
          entity_type: "ee",
          entity_id: Number(targetEE?.ee_id),
          ...(standard_name != null ? { standard_name } : {}),
        },
        show_toast: true,
      }),
    );

    setAttributeSimilarTickets(res?.data || []);
    setAttributeCreateTicketData(res?.data || []);
    return res;
  };

  const handelGetAttributeAiSuggestion = (ticket) => {
    setAttributeSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "EE Component",
    });
    setAttributeOpenAiPanel(true);
    setAttributeSelectedTicket(ticket);
  };

  const [eeDataSelected, setEeDataSelected] = useState(null);
  /* -------------- Handle SW Module functions -------------- */
  const handleEESWModuleAdd = async ({
    variant_ids,
    version,
    name,
    summary,
    description,
    vf_ids,
    supplier_name,
    git_link,
    attributes,
  }) => {
    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails &&
      selectedSingleVariantDetails?.variant_id &&
      selectedEEComponentDetails &&
      selectedEEComponentDetails?.ee_id
    ) {
      let currentTime = new Date().toISOString();

      const response = await dispatch(
        HandleApiActions({
          ...labCar.add_sw_module,
          params: {
            ...(supplier_name && { supplier_name }),
            save_as_draft: false,
            variant_ids,
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            version,
            ...(git_link && { git_link }),
            name,
            ...(summary && { summary }),
            description,
            vf_ids,
            current_time: currentTime,
            attributes: attributes,
          },
          show_toast: true,
        }),
      );
      await getSWModulesList({ force: true });
      // .then((res) => {
      // getSWModulesList();

      // if (res.error) {
      //   return false;
      // } else {
      //   return true;
      // }
      // });
    }

    return false;
  };

  const handleEESWModuleUpdate = ({
    swm_id,
    version,
    will_it_impact,
    reason,
    github_link,
    requires_homologation,
  }) => {
    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails &&
      selectedSingleVariantDetails?.variant_id &&
      selectedEEComponentDetails &&
      selectedEEComponentDetails?.ee_id
    ) {
      let currentTime = new Date().toISOString();

      customConsole({
        project_id: Number(selectedProjectDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentDetails?.ee_id),
        swm_id,
        version,
        current_time: currentTime,
        reason,
        will_it_impact,
      });

      dispatch(
        HandleApiActions({
          ...labCar.update_sw_module,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            swm_id,
            version,
            current_time: currentTime,
            reason,
            will_it_impact,
            requires_homologation,
          },
          show_toast: true,
        }),
      ).then(() => {
        getSWModulesList({ force: true });
      });
    }
  };

  const handleEESWModuleReplace = ({
    supplier_name,
    swm_id,
    version,
    name,
    summary,
    description,
    vf_ids,
    will_it_impact,
    reason,
  }) => {
    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails &&
      selectedSingleVariantDetails?.variant_id &&
      selectedEEComponentDetails &&
      selectedEEComponentDetails?.ee_id
    ) {
      let currentTime = new Date().toISOString();

      customConsole({
        project_id: Number(selectedProjectDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentDetails?.ee_id),
        swm_id,
        version,
        name,
        ...(summary && { summary }),
        description,
        vf_ids,
        current_time: currentTime,
      });

      dispatch(
        HandleApiActions({
          ...labCar.replace_sw_module,
          params: {
            supplier_name: supplier_name,
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            swm_id,
            version,
            name,
            ...(summary && { summary }),
            description,
            vf_ids,
            current_time: currentTime,
            reason,
            will_it_impact,
          },
          show_toast: true,
        }),
      ).then(() => {
        getSWModulesList();
      });
    }
  };

  /* Get EE component change logs */
  const handleGetEEComponentChangeLogs = async ({
    ee_id,
    pageNo,
    pageSize,
  }) => {
    let apiDetails = labCar.get_ee_components_change_logs;

    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails?.variant_id &&
      selectedSingleVariantDetails
    ) {
      customConsole({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          varient_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id,
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id,
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      );
      if (resp.error) {
        setEEChangeLogsList([]);
      } else {
        setEEChangeLogsList(resp?.data);
        setEEChangeLogsTotalCount(resp?.total_count);
      }
    }
  };

  /* Handle dialogs */

  const handleEELogsDialogOpen = (data) => {
    setSelectedEEItem(data);
    setOpenEEComponentChangeLogsDialog(true);

    // handleGetEEComponentChangeLogs({ ee_id: data?.ee_id });

    setEELogsDialogData(data);
  };
  const handleEELogsDialogClose = () => {
    setEELogsDialogData(null);
    setEEChangeLogsList([]);

    setOpenEEComponentChangeLogsDialog(false);
  };

  // const handleMoreClick = (e, itemId) => {
  //   e.stopPropagation(); // Prevent click event from bubbling up
  //   setActiveItemId(activeItemId === itemId ? null : itemId); // Toggle visibility
  // };

  const handleCloseActions = () => {
    setActiveItemId(null); // Close all actions
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleRequestsDialogOpen = (data) => {
    setEeDataSelected(data);
    setOpenRequestsDialog(true);
    getRequests();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setReason(""); // Reset reason
  };

  const handleRequestsDialogClose = () => {
    setOpenRequestsDialog(false);
    setReason(""); // Reset reason
  };

  const handleSubmit = async () => {
    const reqObj = {
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      request_reason: reason,
    };

    try {
      const response = await dispatch(
        HandleApiActions({
          ...editRequestAPI.create_edit_requests,
          params: reqObj,
          show_toast: true,
        }),
      );
      if (!response.error) {
        // Reset form fields
        getRequests();
        setReason("");
        handleDialogClose();
      }
    } catch (error) {
      console.error("Failed to update the reason", error);
    }
  };

  const handleMoreClick = (e, item) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setActiveItemId(item.ee_id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveItemId(null);
  };

  const handleMenuAction = (action, item) => {
    handleCloseMenu(); // Close the menu after action
    if (action === "update") {
      handleSelectedEEComponentUpdateDetails(item);
    } else if (action === "replace") {
      handleSelectedEEComponentReplaceDetails(item);
    } else if (action === "logs") {
      handleEELogsDialogOpen(item);
    } else if (action === "requests") {
      handleRequestsDialogOpen(item);
    } else if (action === "details") {
      setDetailsItem(item); // <-- This updates detailsItem
      setOpenDetailsDialog(true); // <-- This opens the dialog
    } else if (action === "updateAttribute") {
      // Handle update attribute action
      setSelectedEEComponentUpdateDetails(item);
      setOpenAttributeUpdateDialog(true);
    }
  };


  // Details dialog
  const DetailRow = ({ label, value }) => (
    <Box display="flex" gap={1} mb={1}>
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ minWidth: "130px", color: "#333" }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  );

  const getRequests = async () => {
    let apiDetails = editRequestAPI.get_edit_requests;
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(selectedEEComponentDetails?.ee_id),
          is_sent_requests: true,
          page_number: 1,
          page_size: 25,
        },
        show_toast: false,
      }),
    );
    if (resp.error) {
      setGetRequestData([]);
    } else {
      setGetRequestData(resp.data);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 100:
        return "Pending";
      case 200:
        return "Approved";
      case 300:
        return "Rejected";
      default:
        return "Unknown Status";
    }
  };



  const handleActionType1DialogOpen = (ecu) => {
    setSelectedECU(ecu);
    setActionType1DialogOpen(true);
  };

  const handleActionType2DialogOpen = (ecu) => {
    setSelectedECU(ecu);
    setActionType2DialogOpen(true);
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleRedirectionToTypeApproval = () => {
    navigate(
      `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id}&zone=${selectedZone?.zone_position_name}&tab=0`,
    );
    setActionType2DialogOpen(false);
  };

  const getParentEEDetails = async () => {
    // api call for getting parent EE components
    let parentEEResponse = await dispatch(
      HandleApiActions({
        ...labCar.get_parent_ee,
        params: {
          project_id: selectedProjectDetails.project_id,
          parent_project_id: selectedProjectDetails.parent_project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
          parent_variant_id:
            selectedSingleVariantDetails?.parent_variant_details?.variant_id,
        },
      }),
    );
    if (!parentEEResponse.error) {
      setSelectedParentEEDetails(parentEEResponse.data[0] || null);
      setRecreationParentEEList(parentEEResponse.data || []);
      setOpenRecreationEEDialog(true);
    } else {
      showToastMessage({
        type: "error",
        message: parentEEResponse.msg,
      });
      setRecreationParentEEList([]);
      setSelectedParentEEDetails(null);
    }
  };

  const handleEERecreateOpen = async () => {
    await getParentEEDetails();
  };

  const handleRecreateEEClose = () => {
    setOpenRecreationEEDialog(false);
  };

  const handleEEComponentRecreate = () => {
  };

  const zones = [
    "CENTRAL LEFT",
    "CENTRAL RIGHT",
    "FRONT",
    "REAR",
    "FRONT LEFT",
    "FRONT RIGHT",
    "REAR LEFT",
    "REAR RIGHT",
  ];

  const zoneMapping = {
    "CENTRAL LEFT": 0,
    "CENTRAL RIGHT": 1,
    FRONT: 2,
    REAR: 3,
    "FRONT LEFT": 4,
    "FRONT RIGHT": 5,
    "REAR LEFT": 6,
    "REAR RIGHT": 7,
  };



  // Use the mapping to get the index
  const selectedZoneIndex = zoneMapping[selectedZone?.zone_position_name];
  const selectedZoneName =
    selectedZoneIndex !== undefined ? zones[selectedZoneIndex] : null;


  const isSWMFilterActive =
    (swmNameFilter && swmNameFilter?.length > 0) ||
    (swmVersionFilter && swmVersionFilter?.length > 0) ||
    (swmNameFilterSearch && swmNameFilterSearch?.length > 0) ||
    (swmVersionFilterSearch && swmVersionFilterSearch?.length > 0) ||
    (swmSortField && swmSortField !== "name") ||
    (swmSortOrder && swmSortOrder !== "asc");

  // Debounced search for Assembly Number
  const [assemblySearch, setAssemblySearch] = useState("");
  const debounceRef = useRef();

  useEffect(() => {
    // Debounce logic: wait 400ms after user stops typing
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSelectedSearchField("assembly_number");
      setSearchFieldValue(assemblySearch);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [assemblySearch]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: "row",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* EE Component card */}
          <Card
            sx={{
              ...vlcComponentStyle.card,
              display: "flex",
              flexDirection: "column",
              height: "450px",
            }}
          >
            <CardHeader
              sx={vlcComponentStyle.cardHeader}
              title={
                <VlcCardHeader
                  title={"EE Components"}
                  showRecreateIcon={
                    selectedProjectDetails?.parent_project_id &&
                      selectedSingleVariantDetails?.parent_variant_details
                        ?.variant_id
                      ? true
                      : false
                  }
                  onRecreateClick={handleEERecreateOpen}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                    sx={{
                      width: "100%",
                      mt: 1,
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search Assembly Number"
                      variant="outlined"
                      fullWidth
                      sx={componentStyle.searchField.textField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      value={assemblySearch}
                      onChange={(e) => setAssemblySearch(e.target.value)}
                    />
                    {/* </Box> */}
                    <Box>
                      {!supplier && (
                        <AddButtonV2
                          disabled={
                            !selectedVariantsMany || !variantOptionList?.length
                          }
                          handleOnClick={() =>
                            setOpenEEComponentAddDialog(true)
                          }
                        />
                      )}
                    </Box>
                    {/* </Box> */}
                  </Box>
                </VlcCardHeader>
              }
            />

            <CardContent
              sx={{
                ...vlcCardStyles.CardContent,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                p: 0,
                scrollbarColor: "#888 #f1f1f1",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#555",
                },
              }}
            >
              <Box sx={{ p: 0, m: 0 }}>
                {eEComponentsList && eEComponentsList?.length > 0 ? (
                  eEComponentsList?.map((item, index) => (
                    <VlcCardContent
                      onClick={handleSelectedEEComponent}
                      data={item}
                      key={item.ee_id}
                      isSelected={
                        selectedEEComponentDetails?.ee_id === item?.ee_id
                      }
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "start",
                          cursor: "pointer",
                          // backgroundColor: "red",
                        }}
                      >
                        <Box
                          sx={{
                            width: "70%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              p: 1, // ✅ ensures left alignment
                            }}
                          >
                            <Typography
                              sx={{
                                // fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                // fontWeight: 500,
                                ...componentStyle.font.text,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                              }}
                            >
                              <CustomToolTip
                                type="string"
                                length={50}
                                style={{
                                  ...componentStyle.font.text,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                  fontWeight: "bold",
                                }}
                                title={`Assembly No: ${item?.assembly_number ?? ""
                                  }`}
                              >
                                {`Assembly No: ${item?.assembly_number ?? ""}`}
                              </CustomToolTip>
                            </Typography>
                            <Typography
                              sx={{
                                // fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                // fontWeight: 500,
                                ...componentStyle.font.text,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                                color: vlcCardStyles.vlcCardListColor,
                              }}
                            >
                              <CustomToolTip
                                type="string"
                                length={60}
                                style={{
                                  ...componentStyle.font.text,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                  // fontWeight: "bold",
                                }}
                                title={`Description: ${item?.description ?? ""
                                  }`}
                              >
                                {`Description: ${item?.description ?? ""}`}
                              </CustomToolTip>
                            </Typography>

                            <Typography
                              sx={{
                                // fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                // fontWeight: 500,
                                ...componentStyle.font.text,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                                color: vlcCardStyles.vlcCardListColor,
                              }}
                            >
                              <CustomToolTip
                                type="string"
                                length={60}
                                style={{
                                  ...componentStyle.font.text,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                  // fontWeight: "bold",
                                }}
                                title={`Version: ${item?.version ?? ""}`}
                              >
                                {`Version: ${item?.version ?? ""}`}
                              </CustomToolTip>
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                              ml: 0.5,
                              mt: -0.5,
                            }}
                          >
                            {item?.status === false ? (
                              <Box sx={{ pl: 0.5, pb: 0.5 }}>
                                <Chip
                                  onClick={() => setOpenDialogDraftToAdd(true)}
                                  sx={{
                                    borderRadius: 0,
                                    color: "#007CB0",
                                    borderColor: "#007CB0",
                                    borderWidth: 1,
                                    // fontWeight: "bold",
                                    fontSize: "11px",
                                  }}
                                  size="small"
                                  variant="outlined"
                                  label={"Draft"}
                                />
                              </Box>
                            ) : null}
                          </Box>
                          {/* <Box
                          sx={{
                            position: "absolute",
                            bottom: "5px",
                            right: "5px",
                            backgroundColor: "red",
                          }}
                          > */}

                          {item?.variant_ids?.length > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                borderRadius: 0,
                                ml: 0.5,
                                mb: 0.5,
                              }}
                            >
                              {variantOptionList
                                ?.filter(
                                  (variant) =>
                                    variant?.value !==
                                    selectedSingleVariantDetails?.variant_id &&
                                    item?.variant_ids?.includes(variant?.value),
                                )
                                ?.map((colour) => {
                                  return (
                                    <Box sx={{ borderRadius: 0 }}>
                                      <CustomToolTip
                                        type="component"
                                        title={colour?.label}
                                      >
                                        <Skeleton
                                          animation={"false"}
                                          variant="rectangular"
                                          sx={{
                                            backgroundColor: colour?.color,
                                            marginTop: "2px",
                                            marginLeft: "2px",
                                          }}
                                          width={12}
                                          height={12}
                                        />
                                      </CustomToolTip>
                                    </Box>
                                  );
                                })}
                            </Box>
                          ) : null}
                          {/* </Box> */}
                        </Box>
                        <Box sx={{ position: "relative" }}>
                          <Box
                            key={item?.ee_id}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="flex-start"
                            // gap={0.1}
                            >
                              {/* Line 1: Lock/Unlock + More (left aligned) */}
                              <Box
                                display="flex"
                                alignItems="center"
                                // gap={0.1}
                                sx={{
                                  flexWrap: "nowrap",
                                  minWidth: 0,
                                  overflow: "hidden",
                                }}
                              >
                                {/* Error Icons (right aligned) */}
                                <Box
                                  width="100%"
                                  display="flex"
                                  justifyContent="flex-end"
                                >
                                  {item.action_type === 1 ||
                                    item.action_type === 2 ||
                                    item.action_type === 4 ||
                                    item.action_type === 5 ? (
                                    <CustomToolTip
                                      type="component"
                                      title={
                                        item.action_type === 1
                                          ? "Action required in EE Component to EE Component linking"
                                          : item.action_type === 2
                                            ? "Action required in EE Component to Legal requirements"
                                            : item.action_type === 4
                                              ? "Action required in EE Component Attribute"
                                              : "Action pending in EE Component Version"
                                      }
                                    >
                                      <IconButton
                                        size="small"
                                        sx={{ color: "black" }}
                                        onClick={() => {
                                          if (item.action_type === 1)
                                            handleActionType1DialogOpen(item);
                                          else if (item.action_type === 2)
                                            handleActionType2DialogOpen(item);
                                          else if (item.action_type === 4) {
                                            setSelectedEEComponentUpdateDetails(
                                              item,
                                            );
                                            setResumeAttributeImpactFlowTrigger(
                                              (prev) => prev + 1,
                                            );
                                          } else {
                                            setSelectedEEComponentUpdateDetails(
                                              item,
                                            );
                                            setOpenEEComponentUpdateDialog(
                                              true,
                                            );
                                            setResumeVersionImpactFlowTrigger(
                                              (prev) => prev + 1,
                                            );
                                          }
                                        }}
                                      >
                                        <ErrorOutlineIcon
                                          sx={{ fontSize: "18px" }}
                                        />
                                      </IconButton>
                                    </CustomToolTip>
                                  ) : null}
                                </Box>
                                {item?.is_locked ? (
                                  <CustomToolTip type="component" title="Lock">
                                    <IconButton
                                      size="small"
                                      sx={{ color: "black" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleLockUnLockEE({
                                          eeId: item?.ee_id,
                                          isLocked: false,
                                        });
                                      }}
                                    >
                                      <LockIcon sx={{ fontSize: "18px" }} />
                                    </IconButton>
                                  </CustomToolTip>
                                ) : (
                                  <CustomToolTip
                                    type="component"
                                    title="UnLock"
                                  >
                                    <IconButton
                                      size="small"
                                      sx={{ color: "black" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleLockUnLockEE({
                                          eeId: item?.ee_id,
                                          isLocked: true,
                                        });
                                      }}
                                    >
                                      <NoEncryptionGmailerrorredIcon
                                        sx={{ fontSize: "18px" }}
                                      />
                                    </IconButton>
                                  </CustomToolTip>
                                )}

                                <CustomToolTip type="component" title="More">
                                  <IconButton
                                    size="small"
                                    sx={{ color: "black" }}
                                    onClick={(e) => handleMoreClick(e, item)}
                                  >
                                    <MoreVertIcon sx={{ fontSize: "18px" }} />
                                  </IconButton>
                                </CustomToolTip>
                              </Box>
                            </Box>

                            {/* Conditional Menu Rendering */}
                            <Menu
                              anchorEl={anchorEl}
                              open={
                                Boolean(anchorEl) && activeItemId === item.ee_id
                              }
                              onClose={handleCloseMenu}
                              PaperProps={{
                                sx: {
                                  borderRadius: 0, // ✅ remove rounding completely
                                  boxShadow: 1, // optional: remove shadow if you want a flat look
                                },
                              }}
                            >
                              {!item?.is_locked &&
                                (item.is_edit_enabled ||
                                  selectedProjectDetails?.is_user_project_owner) ? (
                                <>
                                  {/* <MenuItem
                                  onClick={() =>
                                    handleMenuAction("details", item)
                                  }
                                  sx={{ ...componentStyle.font.cardText }}
                                >
                                  Details
                                </MenuItem> */}

                                  {/* {item?.action_type === 0 ? (
                                  <MenuItem
                                    onClick={() =>
                                      handleMenuAction("update", item)
                                    }
                                    disabled={
                                      item?.status === true ? false : true
                                    }
                                    sx={{ ...componentStyle.font.cardText }}
                                  >
                                    Update
                                  </MenuItem>
                                ) : null} */}

                                  {item?.action_type === 0 ? (
                                    <MenuItem
                                      onClick={() => {
                                        // handleMenuAction("update", item)
                                        setOpenEditDetailsDialog(true);
                                        setSelectedEEItem(item);
                                      }}
                                      disabled={
                                        item?.status === true ? false : true
                                      }
                                      sx={{ ...componentStyle.font.cardText }}
                                    >
                                      Edit Details
                                    </MenuItem>
                                  ) : null}

                                  {/* {item?.action_type === 0 ? (
                                  <MenuItem
                                    onClick={() =>
                                      handleMenuAction("replace", item)
                                    }
                                    disabled={
                                      item?.status === true ? false : true
                                    }
                                    sx={{ ...componentStyle.font.cardText }}
                                  >
                                    Replace
                                  </MenuItem>
                                ) : null} */}
                                </>
                              ) : (
                                <>
                                  {/* <MenuItem
                                  onClick={() =>
                                    handleMenuAction("details", item)
                                  }
                                  sx={{ ...componentStyle.font.cardText }}
                                >
                                  Details
                                </MenuItem>
                                <MenuItem disabled>Update</MenuItem>
                                <MenuItem disabled>Replace</MenuItem> */}
                                </>
                              )}
                              {!(
                                (
                                  item.is_edit_enabled ||
                                  selectedProjectDetails?.is_user_project_owner
                                )
                                // ||
                                // selectedSingleVariantDetails?.vehicle_status ===
                                //   "After Sales"
                              ) && (
                                  <MenuItem
                                    onClick={() =>
                                      handleMenuAction("requests", item)
                                    }
                                    sx={{ ...componentStyle.font.cardText }}
                                  >
                                    Edit Request
                                  </MenuItem>
                                )}
                              <MenuItem
                                onClick={() => handleMenuAction("logs", item)}
                                sx={{ ...componentStyle.font.cardText }}
                              >
                                Log
                              </MenuItem>
                            </Menu>
                          </Box>
                        </Box>
                      </Box>
                    </VlcCardContent>
                  ))
                ) : (
                  <Typography variant="body1" sx={styles.noDataAvailable}>
                    No data available
                  </Typography>
                )}
              </Box>
            </CardContent>
            {/* <CardActions sx={styles.eeCompCardActions}>
            <Button
              startIcon={<CloseIcon />}
              color="error"
              size="small"
              variant="contained"
            >
              Cancel
            </Button>
          </CardActions> */}
            {totalEECount > 0 && (
              <Paper
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  // p: { xs: "2px", sm: "3px" },
                  minHeight: "40px",
                  borderTop: "1px solid #ddd",
                  borderRadius: "0px !important",
                }}
              >
                <CustomPaginationV2
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handlePageChange}
                  totalItems={totalEECount}
                />
              </Paper>
            )}
          </Card>
          {/* Lock and Un Lock EE dialoug */}
          <CustomDialogBoxV2
            open={openLockUnLockEEDialog}
            onClose={handleCloseLockUnLockEE}
            title={`Reason to ${lockUnLockEEData?.isLocked ? "Lock" : "UnLock"
              }`}
            successButtonName={"Confirm"}
            rejectButtonName={"Cancel"}
            onClickSuccess={handleConfirmLockUnLockEE}
            onClickReject={handleCloseLockUnLockEE}
            actionButtonStyle={"continuous"}
            onDisabled={lockUnLockEEReason?.length < 3}
          >
            <Stack spacing={2} sx={{ width: "350px" }}>
              <CustomFormTextFieldV2
                label="Reason"
                value={lockUnLockEEReason}
                setValue={setLockUnLockEEReason}
                // txtFldStyle={{ width: "270px" }}
                required={true}
              />
            </Stack>
          </CustomDialogBoxV2>

          {/* Edit details dialoug update replace dialog with readio*/}
          <EditDetailsDialog
            openEditDetails={openEditDetailsDialog}
            setOpenEditDetails={setOpenEditDetailsDialog}
            onClick={(type) => {
              setOpenEditDetailsDialog(false);
              handleMenuAction(type, selectedEEItem);
            }}
          />

          {/* <ActionButtonListDependencies
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            openActionButton={openActionButton}
            setOpenActionButton={setOpenActionButton}
            selectedEEComponentDetails={selectedEEComponentDetails}
            handleSelectedEEComponentUpdateDetails={
              handleSelectedEEComponentUpdateDetails
            }
            handleSelectedEEComponentReplaceDetails={
              handleSelectedEEComponentReplaceDetails
            }
          /> */}

          <VlcEEComponentAddDialog
            title={"Add EE Component"}
            supplierOptionsList={supplierOptionsList}
            adminSettingsDetails={adminSettingsDetails}
            selectedZone={selectedZone}
            selectedEEComponentDetails={selectedEEComponentDetails}
            selectedProjectDetails={selectedProjectDetails}
            selectedVariantsMany={selectedVariantsMany}
            setSelectedVariantsMany={setSelectedVariantsMany}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            variantOptionList={variantOptionList}
            selectedZoneDetails={selectedZoneDetails}
            openEEComponentAddDialog={openEEComponentAddDialog}
            setOpenEEComponentAddDialog={setOpenEEComponentAddDialog}
            handleEEComponentAdd={handleEEComponentAdd}
            vsOptionsList={vsOptionsList}
            getEEComponentsList={getEEComponentsList}
            getZoneEECountList={getZoneEECountList}
          />

          <VlcEEComponentUpdateDialog
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEEComponentUpdateDialog={openEEComponentUpdateDialog}
            setOpenEEComponentUpdateDialog={setOpenEEComponentUpdateDialog}
            handleEEComponentUpdate={handleEEComponentUpdate}
            selectedEEComponentUpdateDetails={selectedEEComponentUpdateDetails}
            setSelectedEEComponentUpdateDetails={
              setSelectedEEComponentUpdateDetails
            }
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            isVersionImpactFlow={isVersionImpactFlow}
            setIsVersionImpactFlow={setIsVersionImpactFlow}
            resumeAttributeImpactFlowTrigger={resumeAttributeImpactFlowTrigger}
            resumeVersionImpactFlowTrigger={resumeVersionImpactFlowTrigger}
            onRedirectedActionDialogClose={clearRedirectedEEActionParams}
          />

          <AttributeUpdateDialog
            ref={attributeUpdateDialogRef}
            openAttributeUpdateDialog={openAttributeUpdateDialog}
            setOpenAttributeUpdateDialog={setOpenAttributeUpdateDialog}
            handleCloseAttributeUpdateDialog={() => {
              setOpenAttributeUpdateDialog(false);
              setIsVersionImpactFlow(false);
              clearRedirectedEEActionParams();
            }}
            onClickSuccess={() => {
              setOpenAttributeUpdateDialog(false);
              getEEComponentsList();
            }}
            onIntersectionNo={loadAttributeDecisionMatrixContext}
            onShowUpdateDialog={() => {
              // This callback is called when user clicks Yes in version impact dialog from AttributeUpdateDialog
              setIsVersionImpactFlow(true);
              getEEComponentsList();
              setTimeout(() => {
                setOpenEEComponentUpdateDialog(true);
              }, 300);
            }}
            selectedComponent={selectedEEComponentUpdateDetails}
            componentType={"ee"}
            variantId={selectedSingleVariantDetails?.variant_id}
            reason={reason}
            setReason={setReason}
            reasonError={reasonError}
          />

          {/* used replace dialog for recreation */}
          <RecreateEEDialog
            title={"Recreate EE Component"}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEEComponentReplaceDialog={openRecreationEEDialog}
            setOpenEEComponentReplaceDialog={setOpenRecreationEEDialog}
            handleEEComponentReplace={handleEEComponentRecreate}
            selectedEEComponentReplaceDetails={selectedParentEEDetails}
            setSelectedEEComponentReplaceDetails={
              setSelectedEEComponentReplaceDetails
            }
            getZoneEECountList={getZoneEECountList}
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            vsOptionsList={vsOptionsList}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            recreationParentEEList={recreationParentEEList}
            setRecreationParentEEList={setRecreationParentEEList}
            selectedParentEEDetails={selectedParentEEDetails}
            setSelectedParentEEDetails={setSelectedParentEEDetails}
            onClickAddForRecreate={handleEEComponentRecreate}
            getParentEEDetails={getParentEEDetails}
          />

          <VlcEEComponentReplaceDialog
            title={"Replace EE Component"}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEEComponentReplaceDialog={openEEComponentReplaceDialog}
            setOpenEEComponentReplaceDialog={setOpenEEComponentReplaceDialog}
            handleEEComponentReplace={handleEEComponentReplace}
            selectedEEComponentReplaceDetails={
              selectedEEComponentReplaceDetails
            }
            setSelectedEEComponentReplaceDetails={
              setSelectedEEComponentReplaceDetails
            }
            getZoneEECountList={getZoneEECountList}
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            selectedZoneDetails={selectedZoneDetails}
            vsOptionsList={vsOptionsList}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
          />

          {/* For Draft */}
          <VlcEEComponentReplaceDialog
            title={"Add Draft EE Component"}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEEComponentReplaceDialog={openDialogDraftToAdd}
            setOpenEEComponentReplaceDialog={setOpenDialogDraftToAdd}
            handleEEComponentReplace={handleEEComponentReplace}
            selectedEEComponentReplaceDetails={selectedEEComponentDetails}
            setSelectedEEComponentReplaceDetails={
              setSelectedEEComponentReplaceDetails
            }
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            selectedZoneDetails={selectedZoneDetails}
            vsOptionsList={vsOptionsList}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
          />
        </Box>

        {/* Software module card */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* {isEEComponentSelected && ( */}
          <VlcSWModules
            variantOptionList={variantOptionList}
            heading={selectedEEComponentDetails?.description}
            sWModulesList={isSWMFilterActive ? sortedSWMList : eESWModulesList}
            handleSWModuleAdd={handleEESWModuleAdd}
            handleSWModuleUpdate={handleEESWModuleUpdate}
            handleSWModuleReplace={handleEESWModuleReplace}
            setIsEEComponentSelected={setIsEEComponentSelected}
            setSelectedEEComponentDetails={setSelectedEEComponentDetails}
            selectedProjectDetails={selectedProjectDetails}
            selectedEEComponentDetails={selectedEEComponentDetails}
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            getSWModulesList={getSWModulesList}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            swmSearchFieldValue={swmSearchFieldValue}
            setSWMSearchFieldValue={setSWMSearchFieldValue}
            swmSelectedSearchField={swmSelectedSearchField}
            setSWMSelectedSearchField={setSWMSelectedSearchField}
            page={swmPage}
            setPage={setSwmPage}
            rowsPerPage={swmRowsPerPage}
            setRowsPerPage={setSwmRowsPerPage}
            totalSWMCount={totalSWMCount}
            hasMore={swmHasMore}
            setHasMore={setSwmHasMore}
            swmNameFilter={swmNameFilter}
            setSWMNameFilter={setSWMNameFilter}
            swmVersionFilter={swmVersionFilter}
            setSWMVersionFilter={setSWMVersionFilter}
            swmNameFilterSearch={swmNameFilterSearch}
            setSWMNameFilterSearch={setSWMNameFilterSearch}
            swmVersionFilterSearch={swmVersionFilterSearch}
            setSWMVersionFilterSearch={setSWMVersionFilterSearch}
            swmSortField={swmSortField}
            setSWMSortField={setSWMSortField}
            swmSortOrder={swmSortOrder}
            setSWMSortOrder={setSWMSortOrder}
            swmListToFilter={swmListToFilter}
            filteredSWMList={sortedSWMList}
            sortedSWMList={sortedSWMList}
            onRedirectedActionDialogClose={clearRedirectedSWMActionParams}
          />
          {/* )} */}
        </Box>
      </Box>
      {/* <CustomLogsDialog
        open={openEEComponentChangeLogsDialog}
        handleClose={handleEELogsDialogClose}
        dialogTitle={"Change Logs"}
        dialogContent={eeChangeLogsList}
        data={eeLogsDialogData}
        getLogsData={handleGetEEComponentChangeLogs}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
      /> */}
      <CustomDialogBoxV2
        open={openEEComponentChangeLogsDialog}
        onClose={handleEELogsDialogClose}
        title={"EE Components"}
      >
        <Box sx={{ width: "450px", height: "500px" }}>
          <DetailsLogTab
            logData={eeLogsDialogData}
            ChangeLogsList={eeChangeLogsList}
            getLogsData={handleGetEEComponentChangeLogs}
            selectedComponent={selectedEEItem}
            zone_name={selectedEEComponentDetails?.zone_details?.zone_name}
            title={"Update Draft EE Component"}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEditDetailsDialog={openEEComponentChangeLogsDialog}
            setOpenEditDetailsDialog={setOpenEEComponentChangeLogsDialog}
            handleEEComponentReplace={handleEEComponentReplace}
            selectedEEComponentReplaceDetails={selectedEEComponentDetails}
            setSelectedEEComponentReplaceDetails={
              setSelectedEEComponentReplaceDetails
            }
            totalLogsCount={eeChangeLogsTotalCount}
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            selectedZoneDetails={selectedZoneDetails}
            vsOptionsList={vsOptionsList}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            type={"EE"}
          />
        </Box>
      </CustomDialogBoxV2>

      <Box>
        {/* Dialog for Action Required */}
        {/* <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          BackdropProps={{
            style: {
              backgroundColor: "transparent",
            },
          }}
        >
          <DialogTitle>Edit Accces Required</DialogTitle>
          <DialogContent>
            <Typography mb={2}>
              Request access to update/replace the EE component
            </Typography>
            <CustomTextFieldN
              size={"small"}
              label={"Request"}
              placeholder={"Reason for request"}
              type={"text"}
              fldType={"text5"}
              value={reason}
              setValue={setReason}
              valueError={reasonError}
              setValueError={setReasonError}
              variant={"standard"}
              txtFldStyle={styles.txtStyleR}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDialogClose}
              sx={{ ...appTheme.commonBtnStyle }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              sx={{ ...appTheme.commonBtnStyle }}
              disabled={reason?.length < 5}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog> */}

        {/* Dialog for to show pending requests */}
        {/* <Dialog
          open={false}
          onClose={handleRequestsDialogClose}
          BackdropProps={{
            style: {
              backgroundColor: "transparent",
            },
          }}
        >
          <DialogTitle>Edit Requests</DialogTitle>
          <DialogContent>
            {getRequestsData?.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>S.No</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Reason</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Approver</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Created At</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Updated At</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getRequestsData?.map((request, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{request.request_reason}</TableCell>
                        <TableCell>{getStatusLabel(request.status)}</TableCell>
                        <TableCell>
                          {request?.approved_user?.fullname}
                        </TableCell>
                        <TableCell>
                          {new Date(request.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {new Date(request.updated_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <p>No requests available.</p>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleRequestsDialogClose}
              sx={{ ...appTheme.commonBtnStyle }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog> */}

        <CustomDialogBoxV2
          open={openRequestsDialog}
          onClose={handleRequestsDialogClose}
          title="Edit Requests"
        // paperSx={{ width: "900px", maxWidth: "95vw" }}
        >
          <Box sx={{ width: "450px", height: "300px" }}>
            <RequestsAndLogsTabs
              getRequestsData={getRequestsData}
              getStatusLabel={getStatusLabel}
              reason={reason}
              setReason={setReason}
              reasonError={reasonError}
              setReasonError={setReasonError}
              handleSubmit={handleSubmit}
              ChangeLogsList={eeChangeLogsList}
              totalLogsCount={eeChangeLogsTotalCount}
              onClose={handleRequestsDialogClose}
              selectedProjectDetails={selectedProjectDetails}
              data={eeDataSelected}
            />
          </Box>
        </CustomDialogBoxV2>
        {/* Action Pending Dialog */}
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>EE Component dependency linking is pending!</Typography>
          </Stack>
        </CustomDialogBoxV2>



        {/* Action Pending Dialog */}
        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={handleRedirectionToTypeApproval} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              EE Component intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        <Dialog
          open={showAttributeDecisionMatrix}
          onClose={handleAttributeDecisionMatrixClose}
          fullScreen
          PaperProps={{
            sx: {
              width: "100vw",
              height: "100vh",
              margin: 0,
              borderRadius: 0,
            },
          }}
        >
          <AppBar
            position="sticky"
            sx={{
              backgroundColor: "#26890D",
            }}
          >
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Decision Matrix
              </Typography>
              <IconButton
                color="inherit"
                onClick={handleAttributeDecisionMatrixClose}
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Box
            sx={{
              flex: 1,
              height: "calc(100vh - 64px)",
              overflow: "auto",
              p: 2,
            }}
          >
            {showAttributeDecisionMatrix && (
              <DecisionMatrixPanel
                context={attributeDecisionMatrixContext}
                key={showAttributeDecisionMatrix ? "open" : "closed"}
                onExecuteWorkflow={handleAttributeExecuteWorkflow}
                onViewExecution={handleAttributeViewExecution}
                onClose={handleAttributeDecisionMatrixClose}
                onDecisionMatrixAction={handleAttributeDecisionMatrixAction}
                handelGetSimilerTickets={handelGetAttributeSimilerTickets}
                similarTickets={attributeSimilarTickets}
                setSimilarTickets={setAttributeSimilarTickets}
                entityDetails={attributeSelectedEntity}
                handelGetAiSuggestion={handelGetAttributeAiSuggestion}
                selectedTicket={attributeSelectedTicket}
                setSelectedTicket={setAttributeSelectedTicket}
                openAiPanel={attributeOpenAiPanel}
                setOpenAiPanel={setAttributeOpenAiPanel}
                setCreateTicketData={setAttributeCreateTicketData}
                disabledStandards={attributeDisabledStandards}
              />
            )}
          </Box>
        </Dialog>


      </Box>
    </Box>
  );
}

export default React.memo(VlcEEComponents);

// Styles
const styles = {
  closeButton: {
    color: "black",
    position: "absolute",
    top: { xs: "2px", sm: "4px" },
    right: { xs: "4px", sm: "8px" },
  },
  eeCompMainContainer: {
    flex: "0 0 auto",
    minWidth: 0,
    width: "100%",
    height: "450px",
    minHeight: "450px",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "stretch",
    boxSizing: "border-box",
    marginBottom: { xs: "8px", sm: "10px", md: "15px" },
  },
  eeCompCard: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    boxShadow: 4,
    borderRadius: "3%",
    display: "flex",
    flexDirection: "column",
  },
  eeCompCardHeader: {
    py: { xs: 0.3, sm: 0.5 },
    backgroundColor: "lightgrey",
    borderBottom: "1px solid lightgrey",
    borderRadius: "2%",
    // "& .MuiTypography-root": {
    //   fontSize: { xs: "0.6rem", sm: "0.9rem" },
    // },
  },
  eeCompCardContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarColor: "#888 #f1f1f1",
    scrollbarWidth: "thin",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  eeCompCardActions: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "1px solid lightgrey",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  eeCompsList: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 0.5, sm: 1 },
    pl: { xs: 0.5, sm: 1 },
    pr: { xs: 0.2, sm: 0.5 },
    backgroundColor: "background.paper",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    minHeight: { xs: 44, sm: 54 },
    maxWidth: "100%",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  eeCompsListItemContainer: {
    // flexGrow: 1,
    textAlign: "start",
    my: { xs: 0.5, sm: 1 },
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    // display: "flex",
    // flexDirection: "column",
    gap: 0.5,
    // fontSize: { xs: "0.75rem", sm: "0.85rem" },
  },
  noDataAvailable: {
    textAlign: "center",
    mt: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1rem" },
  },
  txtStyle: {
    width: "270px",
  },
  txtStyleR: {
    width: "270px",
  },
  edRpIconsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: 0.5, sm: 1 },
  },
  customTextFieldN: {
    width: "270px",
  },
  selectTxtStyle: {
    width: "270px",
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    p: { xs: "2px", sm: "3px" },
  },
};

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 26-07-2024
 * @description : EE component add dialog.
 * @param : openEEComponentAddDialog, setOpenEEComponentAddDialog, handleEEComponentAdd, vsOptionsList
 * @return : The rendered EE component add dialog.
 */


/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 26-07-2024
 * @description : EE component update dialog.
 * @param : openEEComponentUpdateDialog, setOpenEEComponentUpdateDialog, handleEEComponentUpdate, selectedEEComponentUpdateDetails, setSelectedEEComponentUpdateDetails,
 * @return : The rendered EE component update dialog.
 */
function VlcEEComponentUpdateDialog({
  variantOptionList,
  openEEComponentUpdateDialog,
  setOpenEEComponentUpdateDialog,
  handleEEComponentUpdate,
  selectedEEComponentUpdateDetails,
  setSelectedEEComponentUpdateDetails,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getEEComponentsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  isVersionImpactFlow,
  setIsVersionImpactFlow,
  resumeAttributeImpactFlowTrigger,
  resumeVersionImpactFlowTrigger,
  onRedirectedActionDialogClose,
}) {
  /* Update EE Components Fields */
  // hooks
  let navigate = useNavigate();
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [showAttributeConfirmation, setShowAttributeConfirmation] =
    useState(false); // State for attribute impact dialog
  const [openUpdateFlowAttributeDialog, setOpenUpdateFlowAttributeDialog] =
    useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false); // State to track confirmation dialog
  const [
    isEditAttributeVersionDependencyFlow,
    setIsEditAttributeVersionDependencyFlow,
  ] = useState(false);
  const [
    isAttributeImpactUpdatePersisted,
    setIsAttributeImpactUpdatePersisted,
  ] = useState(false);
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);

  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  //Rule set
  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  // Rule set question flow
  const [ruleSetTree, setRuleSetTree] = useState(null);
  // path: [{ node: {type,data}, answer: 'yes'|'no'|null }]
  const [rulePath, setRulePath] = useState([]);

  const [showDecisionMatrix, setShowDecisionMatrix] = useState(false);
  const [decisionMatrixContext, setDecisionMatrixContext] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [openAiPanel, setOpenAiPanel] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({});
  const [disabledStandards, setDisabledStandards] = useState([]);
  const dispatch = useDispatch();
  const [disableActionButton, setDisableActionButton] = useState(false);
  const lastResumeAttributeImpactFlowTriggerRef = useRef(0);
  const lastResumeVersionImpactFlowTriggerRef = useRef(0);

  useEffect(() => {
    if (
      openEEComponentUpdateDialog == true &&
      selectedEEComponentUpdateDetails
    ) {

      setVersion(selectedEEComponentUpdateDetails?.version || "");
      setSelectedVariantsMany(
        variantOptionList
          ?.filter(
            (variant) =>
              variant?.value !== selectedSingleVariantDetails?.variant_id &&
              selectedEEComponentUpdateDetails?.variant_ids
                ?.map((variant) => variant)
                ?.includes(variant?.value),
          )
          ?.map((data) => data?.value) || [],
      );
      // setDiscp("");
      setDiscpError("");
    } else {
      // setDiscp("");
      setDiscpError("");
    }
  }, [openEEComponentUpdateDialog, selectedEEComponentUpdateDetails]);

  useEffect(() => {
    if (
      resumeAttributeImpactFlowTrigger &&
      resumeAttributeImpactFlowTrigger !==
      lastResumeAttributeImpactFlowTriggerRef.current &&
      selectedEEComponentUpdateDetails
    ) {
      lastResumeAttributeImpactFlowTriggerRef.current =
        resumeAttributeImpactFlowTrigger;
      setShowAttributeConfirmation(false);
      setShowConfirmation(false);
      setShowIntersectionImpactPrompt(false);
      setIsVersionImpactFlow(false);
      setIsEditAttributeVersionDependencyFlow(false);
      setIsAttributeImpactUpdatePersisted(true);
      // setTimeout(() => {
      setOpenUpdateFlowAttributeDialog(true);
      // }, 200);
    }
  }, [resumeAttributeImpactFlowTrigger, selectedEEComponentUpdateDetails]);

  useEffect(() => {
    if (
      resumeVersionImpactFlowTrigger &&
      resumeVersionImpactFlowTrigger !==
      lastResumeVersionImpactFlowTriggerRef.current &&
      selectedEEComponentUpdateDetails
    ) {
      lastResumeVersionImpactFlowTriggerRef.current =
        resumeVersionImpactFlowTrigger;
      setShowAttributeConfirmation(false);
      setOpenUpdateFlowAttributeDialog(false);
      setShowConfirmation(false);
      setShowIntersectionImpactPrompt(false);
      setIsAttributeImpactUpdatePersisted(false);
      setIsEditAttributeVersionDependencyFlow(false);
      setIsVersionImpactFlow(true);
    }
  }, [resumeVersionImpactFlowTrigger, selectedEEComponentUpdateDetails]);

  /* -------------- Update EE Component -------------- */
  useEffect(() => {
    if (openEEComponentUpdateDialog) {
      setDiscp("");
    }
  }, [openEEComponentUpdateDialog]);

  const isDecisionMatrixContextValid = (data) => {
    if (!data?.standards || data.standards.length === 0) {
      return false;
    }

    return data.standards.some((standard) => {
      if (!standard.rule_sets || standard.rule_sets.length === 0) {
        return false;
      }

      return standard.rule_sets.some(
        (ruleSet) => ruleSet.workflows && ruleSet.workflows.length > 0,
      );
    });
  };

  const loadDecisionMatrixContext = async () => {
    // 1. Fetch context based on component
    const standardsRes = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.decision_matrix_get_context,
        params: {
          component_type: "EE",
          component_id: selectedEEComponentDetails.ee_id,
          project_id: selectedProjectDetails.project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
        },
        show_toast: false,
      }),
    );
    setOpenEEComponentUpdateDialog(false);
    setShowAttributeConfirmation(false); // Reset attribute confirmation on close
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);

    const contextData = standardsRes?.data;

    const isValid = isDecisionMatrixContextValid(contextData);

    if (!isValid) {
      console.warn(
        "Invalid decision matrix context, cannot proceed:",
        contextData,
      );
      setDecisionMatrixContext(null);
      setShowDecisionMatrix(false);
      return;
    }

    setDecisionMatrixContext(standardsRes?.data);

    setShowDecisionMatrix(true);
  };

  const handleExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_start,
          params: {
            workflowId: payload.workflowId,
            version: payload.version,
          },
          show_toast: false,
        }),
      );

      setExecutionId(res.executionId);

      return res;
    }

    if (mode === "COMPLETE") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_node_complete,
          params: {
            executionId: payload.executionId,
            nodeId: payload.nodeId,
            input: payload.input,
          },
          show_toast: false,
        }),
      );

      return res;
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleViewExecution = (executionId) => {
    // navigate / open drawer / open audit view
  };

  const clearUpdateEEComponentFields = () => {
    setSelectedVariantsMany([]);
    setVersion("");
    setVersionError("");
    // setDiscp("");
    setDiscpError("");
  };

  const isUpdateEEComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    return (
      !versionError &&
      version?.trim() !== selectedEEComponentUpdateDetails?.version &&
      version?.trim()?.length >= 3 &&
      regexMin3?.test(version?.trim()) &&
      discp?.trim()?.length >= 3
    );
  };

  const handleCloseEEComponentUpdate = () => {
    setOpenEEComponentUpdateDialog(false);
    onRedirectedActionDialogClose?.();

    clearUpdateEEComponentFields();
    setSelectedEEComponentUpdateDetails(null);
    setShowAttributeConfirmation(false); // Reset attribute confirmation on close
    setOpenUpdateFlowAttributeDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
    setIsVersionImpactFlow(false);
    setIsEditAttributeVersionDependencyFlow(false);
    setIsAttributeImpactUpdatePersisted(false);
    setDisableActionButton(false);
    // setDiscp("");
  };

  const handleUpdateClick = () => {
    if (isVersionImpactFlow) {
      updateVersionImpactEEComponent();
      return;
    }

    setShowAttributeConfirmation(true);
  };

  const updateVersionImpactEEComponent = async () => {
    // This function handles the update when coming from the attribute edit version impact flow
    setDisableActionButton(true);

    try {
      const reqObj = {
        current_time: new Date().toISOString(),
        project_id: Number(selectedEEComponentUpdateDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
        variant_ids: selectedVariantsMany?.map(Number),
        version: version?.trim(),
        will_it_impact: true,
        reason: discp,
      };

      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_ee_component,
          params: reqObj,
          show_toast: true,
        }),
      );

      if (response?.error) {
        setDisableActionButton(false);
        return;
      }

      await dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: {
            current_time: new Date().toISOString(),
            project_id: Number(selectedEEComponentUpdateDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
            is_dependency_linking: false,
            dependency: true,
          },
          show_toast: false,
        }),
      );

      setIsVersionImpactFlow(false);
      setIsEditAttributeVersionDependencyFlow(false);
      handleCloseEEComponentUpdate();
      setActionType1DialogOpen(true);
      getEEComponentsList();
      getSWModulesList();
    } catch (error) {
      console.error("Failed to update", error);
    }

    setDisableActionButton(false);
  };

  const handleEditAttributeVersionDependencyYes = async () => {
    setIsEditAttributeVersionDependencyFlow(false);
    await confirmUpdateAndRedirect();
  };

  const handleEditAttributeVersionIntersectionNo = async () => {
    setShowIntersectionImpactPrompt(false);
    setIsEditAttributeVersionDependencyFlow(false);
    await updateChangesWithoutStandardAndClose();
    handleCloseEEComponentUpdate();
  };

  const handleEditAttributeVersionIntersectionYes = async () => {
    setIsEditAttributeVersionDependencyFlow(false);
    await confirmUpdateAndRedirect({ willImptIntrOnly: true });
  };

  const handlePersistedAttributeDependencyYes = async () => {
    setDisableActionButton(true);
    setShowConfirmation(false);

    try {
      await dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: {
            current_time: new Date().toISOString(),
            project_id: Number(selectedEEComponentUpdateDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
            is_dependency_linking: false,
            dependency: true,
          },
          show_toast: false,
        }),
      );

      setIsAttributeImpactUpdatePersisted(false);
      handleCloseEEComponentUpdate();
      getEEComponentsList();
      getSWModulesList();
    } catch (error) {
      console.error("Failed to update action type", error);
      setDisableActionButton(false);
    }
  };

  const handlePersistedAttributeIntersectionNo = () => {
    setShowIntersectionImpactPrompt(false);
    setIsAttributeImpactUpdatePersisted(false);
    handleCloseEEComponentUpdate();
  };

  const handlePersistedAttributeIntersectionYes = async () => {
    setDisableActionButton(true);
    setShowIntersectionImpactPrompt(false);

    try {
      await dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: {
            current_time: new Date().toISOString(),
            project_id: Number(selectedEEComponentUpdateDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
            is_dependency_linking: false,
            intersection: true,
          },
          show_toast: false,
        }),
      );

      setIsAttributeImpactUpdatePersisted(false);
      handleCloseEEComponentUpdate();
      getEEComponentsList();
      getSWModulesList();
    } catch (error) {
      console.error("Failed to update action type", error);
      setDisableActionButton(false);
    }
  };

  const handleUpdateEEComponent = ({ }) => {
    if (
      selectedEEComponentUpdateDetails &&
      selectedEEComponentUpdateDetails?.ee_id
    ) {
      setShowAttributeConfirmation(true); // Show attribute confirmation on update button click
    }
  };

  const confirmUpdateAndRedirect = async ({
    willImptIntrOnly = false,
    showAttributeDialog = false,
  } = {}) => {
    setDisableActionButton(true);

    try {
      // If showAttributeDialog is true, mark attribute impact and open the edit attribute dialog.
      if (showAttributeDialog) {
        // Close attribute confirmation dialog
        setShowAttributeConfirmation(false);

        const reqObj = {
          current_time: new Date().toISOString(),
          project_id: Number(selectedEEComponentUpdateDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
          variant_ids: selectedVariantsMany?.map(Number),
          version: version?.trim(),
          will_it_impact: true,
          reason: discp,
        };

        const response = await dispatch(
          HandleApiActions({
            ...labCar.update_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        );

        if (response?.error) {
          setDisableActionButton(false);
          return;
        }
        setOpenEEComponentUpdateDialog(false);
        setIsAttributeImpactUpdatePersisted(true);

        await dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: {
              current_time: new Date().toISOString(),
              project_id: Number(selectedEEComponentUpdateDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
              is_dependency_linking: false,
              attribute: true,
            },
            show_toast: false,
          }),
        );

        // setTimeout(() => {
        setOpenUpdateFlowAttributeDialog(true);
        // }, 200);
        getEEComponentsList();
        getSWModulesList();
      } else {
        // For non-attribute updates, proceed with update_ee_component API
        const reqObj = {
          current_time: new Date().toISOString(),
          project_id: Number(selectedEEComponentUpdateDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
          variant_ids: selectedVariantsMany?.map(Number),
          version: version?.trim(),
          will_it_impact: true,
          reason: discp,
        };

        const response = await dispatch(
          HandleApiActions({
            ...labCar.update_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        );

        if (!response.error) {
          handleCloseEEComponentUpdate();
          if (willImptIntrOnly) {
            await dispatch(
              HandleApiActions({
                ...labCar.update_action_type,
                params: {
                  current_time: new Date().toISOString(),
                  project_id: Number(
                    selectedEEComponentUpdateDetails?.project_id,
                  ),
                  variant_id: Number(selectedSingleVariantDetails?.variant_id),
                  ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
                  is_dependency_linking: true,
                },
                show_toast: false,
              }),
            );
          }
          getEEComponentsList();
          getSWModulesList();
        }
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
    setDisableActionButton(false);
  };

  const handleCloseEEComponentUpdateDialog = () => {
    setOpenEEComponentUpdateDialog(false);
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedVariantsMany?.map(Number),
      project_id: Number(selectedEEComponentUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
      version: version?.trim(),
      will_it_impact: false,
      requires_homologation: false,
      reason: discp,
    };

    try {
      // Await the dispatch to ensure you're getting the response
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_ee_component,
          params: reqObj,
          show_toast: true,
        }),
      );
      getEEComponentsList();
      getSWModulesList();
      // setDiscp("");
      // if (!response.error) {
      //   handleCloseEEComponentUpdate();
      // }
      if (response?.data?.snow_resp) {
        // await loadDecisionMatrixContext();
        // setShowDecisionMatrix(true);
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const updateChangesAndClose = async () => {
    // Get requires_homologation value from the last node in the rule path
    const lastNode = rulePath[rulePath?.length - 1]?.node;
    const requiresHomologation = lastNode?.data?.requires_homologation || false;

    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedVariantsMany?.map(Number),
      project_id: Number(selectedEEComponentUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
      version: version?.trim(),
      will_it_impact: false,
      requires_homologation: requiresHomologation,
      reason: discp,
    };

    try {
      // Await the dispatch to ensure you're getting the response
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_ee_component,
          params: reqObj,
          show_toast: true,
        }),
      );
      getEEComponentsList();
      getSWModulesList();
      if (!response.error) {
        handleCloseEEComponentUpdate();
      }
      if (response?.data?.snow_resp) {
        await loadDecisionMatrixContext();
        setShowDecisionMatrix(true);
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleRedirectionToTypeApproval = () => {
    navigate(
      `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id}&zone=${selectedZone?.zone_position_name}&tab=0`,
    );
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  const handleChangeImpactStatus = () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),

      is_dependency_linking: true,
      module_type: "MODULE LINKING",
    };
  };

  useEffect(() => {
    if (
      openEEComponentUpdateDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      const currentOptionValue =
        (variantOptionList || [])?.find((v) => String(v?.value) === String(id))
          ?.value ?? id;
      setSelectedVariantsMany((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr
          ?.map(String)
          ?.includes(String(currentOptionValue));
        if (hasId) return prevArr;
        return [currentOptionValue, ...prevArr];
      });
    }
  }, [
    openEEComponentUpdateDialog,
    selectedSingleVariantDetails,
    setSelectedVariantsMany,
  ]);

  const updateComponentAndCheckForDecisionMatrix = async ({
    standardCode,
  } = {}) => {
    try {

      const reCreateTicketParams = {
        details: createTicketData,
        reason: discp,
      };

      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.re_create_ticket_v1,
          params: reCreateTicketParams,
          show_toast: true,
        }),
      );

      if (!res?.error && standardCode) {
        handleTicketCreationSuccess(standardCode);
      }
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleTicketCreationSuccess = (standardCode) => {
    setDisabledStandards((prev) => [...new Set([...prev, standardCode])]);
  };

  //function to handel a get a list of tickets for a legal requirements
  const handelGetSimilerTickets = async ({ standard_name } = {}) => {
    try {
      const reCreateTicketParams = {
        execution_id: executionId,
        entity_type: "ee",
        entity_id: Number(selectedEEComponentUpdateDetails?.ee_id),
        ...(standard_name != null ? { standard_name } : {}),
      };

      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_similar_tickets,
          params: reCreateTicketParams,
          show_toast: true,
        }),
      );

      setSimilarTickets(res?.data || []);
      setCreateTicketData(res?.data || []);
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handelGetAiSuggestion = (ticket) => {
    setSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "EE Component",
    });
    setOpenAiPanel(true);
    setSelectedTicket(ticket);
  };

  const handelCloseDecisionMatrix = () => {
    setShowDecisionMatrix(false);
    setDecisionMatrixContext(null);
    setRulePath([]);
    setSimilarTickets([]);
    setSelectedEntity(null);
    setSelectedTicket(null);
    setCreateTicketData({});
    setDisabledStandards([]);
  };
  return (
    <Box>
      <CustomDialogBoxV2
        open={openEEComponentUpdateDialog}
        onClose={handleCloseEEComponentUpdate}
        title={"Update EE Component"}
        onClickSuccess={handleUpdateClick} // Trigger the update process
        onDisabled={!isUpdateEEComponentValid()}
        onClickReject={handleCloseEEComponentUpdate}
        actionButtonStyle={"continuous"}
        successButtonName={"Update"}
        rejectButtonName={"Cancel"}
      >
        <Stack
          spacing={2}
          mb={2}
          justifyContent={"center"}
          sx={{ width: "450px" }}
        >
          <CustomFormTextFieldV2
            size={"small"}
            label={"Version"}
            required={true}
            // placeholder={"Version*"}
            type={"text"}
            fldType={"textR"}
            value={version}
            setValue={(value) => value?.length <= 40 && setVersion(value)}
            // valueError={versionError}
            // setValueError={setVersionError}
            maxLength={40}
          />
          <MultiselectConnectorV2
            // multiple
            selectedValues={selectedVariantsMany}
            setSelectedValues={setSelectedVariantsMany}
            label={"Select Variants"}
            optionsList={(variantOptionList || [])?.filter((variants) => {
              const currentId = selectedSingleVariantDetails?.variant_id;
              const alreadyAdded = (
                selectedEEComponentUpdateDetails?.variant_ids || []
              )
                ?.map((v) => String(v))
                ?.includes(String(variants?.value));
              return (
                alreadyAdded || String(variants?.value) === String(currentId)
              );
            })}
            immutableValues={(() => {
              const opt = (variantOptionList || [])?.find(
                (v) =>
                  String(v?.value) ===
                  String(selectedSingleVariantDetails?.variant_id),
              );
              return opt && opt.value != null ? [opt.value] : [];
            })()}
            placeholder={false}
          />
          <CustomFormTextFieldV2
            size={"small"}
            label={"Reason"}
            required={true}
            // placeholder={"Reason to update*"}
            type={"text"}
            fldType={"textO"}
            value={discp}
            setValue={(value) => value?.length <= 100 && setDiscp(value)}
            valueError={discpError}
            setValueError={setDiscpError}
            maxLength={100}
          />

          <CustomDialogBoxV2
            open={showAttributeConfirmation}
            onClose={() => {
              setShowAttributeConfirmation(false);
            }}
            title="Action"
            dialogActions={
              <Box
                sx={{
                  display: "flex",
                  width: "350px",
                  justifyContent: "space-between",
                  height: "50px",
                }}
              >
                <CustomButtonV2
                  size="small"
                  type="cancel"
                  btnName="No"
                  handleOnClick={() => {
                    setShowAttributeConfirmation(false);

                    setTimeout(() => {
                      setShowConfirmation(true);
                    }, 200);
                  }}
                />
                <CustomButtonV2
                  size="small"
                  type="submit"
                  btnName="Yes"
                  handleOnClick={() =>
                    confirmUpdateAndRedirect({ showAttributeDialog: true })
                  }
                  disabled={disableActionButton}
                />
              </Box>
            }
          >
            <Box
              sx={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  // ...componentStyle.font.text,
                  fontSize: "15px",
                }}
              >
                Will it impact the existing attributes?
              </Typography>
            </Box>
          </CustomDialogBoxV2>
        </Stack>
      </CustomDialogBoxV2>

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openUpdateFlowAttributeDialog}
        setOpenAttributeUpdateDialog={setOpenUpdateFlowAttributeDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenUpdateFlowAttributeDialog(false);
          onRedirectedActionDialogClose?.();
        }}
        onClickSuccess={() => {
          getEEComponentsList();
          setOpenUpdateFlowAttributeDialog(false);
        }}
        selectedComponent={selectedEEComponentUpdateDetails}
        componentType={"ee"}
        isAttributeImpactFlow
        variantId={selectedSingleVariantDetails?.variant_id}
        onIntersectionNo={loadDecisionMatrixContext}
        attributeImpactActionType={{ dependency: true }}
        onAttributeImpactComplete={() => {
          getEEComponentsList();
          setOpenUpdateFlowAttributeDialog(false);
          setActionType1DialogOpen(true);
        }}
      />

      <CustomDialogBoxV2
        open={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
        }}
        title="Action"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "350px",
              justifyContent: "space-between",
              height: "50px",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="No"
              handleOnClick={() => {
                setShowConfirmation(false);

                setTimeout(() => {
                  setShowIntersectionImpactPrompt(true);
                }, 200);
              }}
            />
            <CustomButtonV2
              size="small"
              type="submit"
              btnName="Yes"
              handleOnClick={
                isAttributeImpactUpdatePersisted
                  ? handlePersistedAttributeDependencyYes
                  : isEditAttributeVersionDependencyFlow
                    ? handleEditAttributeVersionDependencyYes
                    : confirmUpdateAndRedirect
              }
              disabled={disableActionButton}
            />
          </Box>
        }
      >
        <Box
          sx={{
            width: "350px",
            height: "50px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              // ...componentStyle.font.text,
              fontSize: "15px",
            }}
          >
            Will it impact the existing dependencies?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={showIntersectionImpactPrompt}
        onClose={() => {
          setShowIntersectionImpactPrompt(false);
        }}
        title="Action"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "350px",
              justifyContent: "space-between",
              height: "50px",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="No"
              handleOnClick={() => {
                if (isAttributeImpactUpdatePersisted) {
                  handlePersistedAttributeIntersectionNo();
                  return;
                }

                if (isEditAttributeVersionDependencyFlow) {
                  handleEditAttributeVersionIntersectionNo();
                  return;
                }

                loadDecisionMatrixContext();
                updateChangesWithoutStandardAndClose();
              }}
            />
            <CustomButtonV2
              size="small"
              type="submit"
              btnName="Yes"
              handleOnClick={() =>
                isAttributeImpactUpdatePersisted
                  ? handlePersistedAttributeIntersectionYes()
                  : isEditAttributeVersionDependencyFlow
                    ? handleEditAttributeVersionIntersectionYes()
                    : confirmUpdateAndRedirect({ willImptIntrOnly: true })
              }
            />
          </Box>
        }
      >
        <Box
          sx={{
            width: "350px",
            height: "50px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              // ...componentStyle.font.text,
              fontSize: "15px",
            }}
          >
            Will it impact the existing intersection?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <Box>
        {/* Action Pending Dialog */}
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              EE Component dependency linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        {/* Action Pending Dialog */}
        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={handleRedirectionToTypeApproval} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              EE Component intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>



        <Dialog
          open={showDecisionMatrix}
          onClose={handelCloseDecisionMatrix}
          fullScreen
          PaperProps={{
            sx: {
              width: "100vw",
              height: "100vh",
              margin: 0,
              borderRadius: 0,
            },
          }}
        >
          {/* Deloitte AppBar */}
          <AppBar
            position="sticky"
            sx={{
              backgroundColor: "#26890D",
            }}
          >
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Decision Matrix
              </Typography>
              <IconButton color="inherit" onClick={handelCloseDecisionMatrix}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* Body */}
          <Box
            sx={{
              flex: 1,
              height: "calc(100vh - 64px)",
              overflow: "auto",
              p: 2,
            }}
          >
            {showDecisionMatrix && (
              <DecisionMatrixPanel
                context={decisionMatrixContext}
                key={showDecisionMatrix ? "open" : "closed"}
                onExecuteWorkflow={handleExecuteWorkflow}
                onViewExecution={handleViewExecution}
                onClose={handelCloseDecisionMatrix}
                onDecisionMatrixAction={
                  updateComponentAndCheckForDecisionMatrix
                }
                handelGetSimilerTickets={handelGetSimilerTickets}
                similarTickets={similarTickets}
                setSimilarTickets={setSimilarTickets}
                entityDetails={selectedEntity}
                handelGetAiSuggestion={handelGetAiSuggestion}
                selectedTicket={selectedTicket}
                setSelectedTicket={setSelectedTicket}
                openAiPanel={openAiPanel}
                setOpenAiPanel={setOpenAiPanel}
                setCreateTicketData={setCreateTicketData}
                disabledStandards={disabledStandards}
              />
            )}
          </Box>
        </Dialog>

        {/* Rule set list dialog */}
      </Box>
    </Box>
  );
}

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 26-07-2024
 * @description : EE component replace dialog.
 * @param : openEEComponentReplaceDialog, setOpenEEComponentReplaceDialog, handleEEComponentReplace, selectedEEComponentReplaceDetails, setSelectedEEComponentReplaceDetails, vsOptionsList
 * @return : The rendered EE component replace dialog.
 */
function VlcEEComponentReplaceDialog({
  title,
  getZoneEECountList,
  openEEComponentReplaceDialog,
  setOpenEEComponentReplaceDialog,
  handleEEComponentReplace,
  selectedEEComponentReplaceDetails,
  setSelectedEEComponentReplaceDetails,
  vsOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedZoneDetails,
  selectedEEComponentDetails,
  getEEComponentsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  supplierOptionsList,
}) {
  const navigate = useNavigate();
  /* Replace EE Components Fields */
  const [assemblyNo, setAssemblyNo] = useState("");

  const [assemblyNoError, setAssemblyNoError] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierNameError, setSupplierNameError] = useState("");
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVSIds, setSelectedVSIds] = useState([]);
  const [vcId, setVcId] = useState("");
  const [vcIdError, setVcIdError] = useState(false);
  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);
  const [replaceTabValue, setReplaceTabValue] = useState(0);

  const [selectedASIL, setSelectedASIL] = useState(null);
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [selectedECU, setSelectedECU] = useState(null);
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  //Rule set
  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);

  const [selectedRuleSet, setSelectedRuleSet] = useState("");

  // Rule set question flow
  const [ruleSetTree, setRuleSetTree] = useState(null);
  // path: [{ node: {type,data}, answer: 'yes'|'no'|null }]
  const [rulePath, setRulePath] = useState([]);
  const [decisionMatrixContext, setDecisionMatrixContext] = useState(null);
  const [showDecisionMatrix, setShowDecisionMatrix] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openAiPanel, setOpenAiPanel] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({});
  const [disabledStandards, setDisabledStandards] = useState([]);
  const dispatch = useDispatch();
  const [disableActionButton, setDisableActionButton] = useState(false);

  const handleEEAttributeEdit = useCallback((data = {}) => {
    const added = Array.isArray(data?.added) ? data.added : [];
    const edited = Array.isArray(data?.edited) ? data.edited : [];
    const deleted = Array.isArray(data?.deleted) ? data.deleted : [];

    setAttributeFormData((prev) => {
      const nextData = { added, edited, deleted };
      return JSON.stringify(prev) === JSON.stringify(nextData)
        ? prev
        : nextData;
    });
  }, []);

  const handleSelectedVehicleSystem = useCallback((vehicleSystemId) => {
    setSelectedVSIds(vehicleSystemId ? [Number(vehicleSystemId)] : []);
    setVcId("");
    setVcIdError(false);
  }, []);

  const handleSelectedVehicleComponent = useCallback((vehicleComponentId) => {
    setVcId(vehicleComponentId || "");
    setVcIdError(false);
  }, []);

  useEffect(() => {
    if (
      openEEComponentReplaceDialog == true &&
      selectedEEComponentReplaceDetails
    ) {
      const selectedVehicleSystemIds = (
        selectedEEComponentReplaceDetails?.vehicle_systems || []
      )
        ?.map((vsId) => Number(vsId))
        ?.filter((vsId) => !isNaN(vsId));
      const selectedVehicleSystemIdFromDetails = Number(
        selectedEEComponentReplaceDetails?.vehicle_system_details?.vs_id,
      );

      setAssemblyNo(selectedEEComponentReplaceDetails?.assembly_number || "");
      setSupplierName(selectedEEComponentReplaceDetails?.supplier_name || "");
      setVersion(selectedEEComponentReplaceDetails?.version || "");
      setDescription(selectedEEComponentReplaceDetails?.description || "");
      setSummary(selectedEEComponentReplaceDetails?.summary || "");
      // setSelectedVSIds(
      //   selectedVehicleSystemIds.length > 0
      //     ? selectedVehicleSystemIds
      //     : !isNaN(selectedVehicleSystemIdFromDetails)
      //       ? [selectedVehicleSystemIdFromDetails]
      //       : [],
      // );
      setSelectedVSIds(
        selectedEEComponentReplaceDetails?.vehicle_component_details?.vs_id
          ? [
            selectedEEComponentReplaceDetails?.vehicle_component_details
              ?.vs_id,
          ]
          : [],
      );
      setVcId(
        selectedEEComponentReplaceDetails?.vehicle_component_details?.vc_id ||
        "",
      );
      setVcIdError(false);
      setSelectedASIL(
        selectedEEComponentReplaceDetails?.other_fields?.asil_class || null,
      );
      // setDiscp(selectedEEComponentReplaceDetails?.reason || "");
      setSelectedVariantsMany(
        variantOptionList
          ?.filter(
            (variant) =>
              variant?.value !== selectedSingleVariantDetails?.variant_id &&
              selectedEEComponentReplaceDetails?.variant_ids
                ?.map((variant) => variant)
                ?.includes(variant?.value),
          )
          ?.map((data) => data?.value) || [],
      );
    }
  }, [openEEComponentReplaceDialog, selectedEEComponentReplaceDetails]);

  useEffect(() => {
    if (openEEComponentReplaceDialog) {
      setDiscp("");
      setDiscpError("");
      setReplaceTabValue(0);
    }
  }, [openEEComponentReplaceDialog]);

  const clearReplaceEEComponentFields = () => {
    setAssemblyNo("");
    setAssemblyNoError("");
    setSupplierName("");
    setSupplierNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSelectedVSIds([]);
    setVcId("");
    setVcIdError(false);
    setSelectedASIL(null);
    // setDiscp("");
    setDiscpError("");
    setSelectedVariantsMany([]);
    setAttributeFormData({ added: [], edited: [], deleted: [] });
  };

  const normalizeOptionalId = (id) => {
    if (id === null || id === undefined || id === "") return null;

    const numericId = Number(id);
    return Number.isNaN(numericId) ? null : numericId;
  };

  const getExistingVehicleComponentId = () => {
    const vehicleComponentDetails =
      selectedEEComponentReplaceDetails?.vehicle_component_details;

    return normalizeOptionalId(
      Array.isArray(vehicleComponentDetails)
        ? vehicleComponentDetails[0]?.vc_id
        : vehicleComponentDetails?.vc_id,
    );
  };

  const getExistingVehicleSystemIds = () => {
    const vehicleSystemIds = (
      selectedEEComponentReplaceDetails?.vehicle_systems || []
    )
      ?.map(normalizeOptionalId)
      ?.filter((id) => id !== null);

    if (vehicleSystemIds.length > 0) return vehicleSystemIds;

    const vehicleSystemId = normalizeOptionalId(
      selectedEEComponentReplaceDetails?.vehicle_system_details?.vs_id,
    );

    return vehicleSystemId !== null ? [vehicleSystemId] : [];
  };

  const haveVehicleSystemsChanged = () => {
    const nextVehicleSystemIds = (selectedVSIds || [])
      ?.map(normalizeOptionalId)
      ?.filter((id) => id !== null);
    const existingVehicleSystemIds = getExistingVehicleSystemIds();

    return (
      nextVehicleSystemIds.length !== existingVehicleSystemIds.length ||
      nextVehicleSystemIds.some((id) => !existingVehicleSystemIds.includes(id))
    );
  };

  const isReplaceEEComponentDetailsValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    return (
      !assemblyNoError &&
      !supplierNameError &&
      !versionError &&
      !descriptionError &&
      // selectedVSIds?.length > 0 &&
      // vcId !== "" &&
      // vcId != null &&
      // !vcIdError &&
      selectedASIL?.length > 0 &&
      regexMin3?.test(assemblyNo?.trim()) &&
      regexMin3?.test(version?.trim()) &&
      assemblyNo?.trim()?.length >= 3 &&
      version?.trim()?.length >= 3 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3) &&
      (assemblyNo?.trim() !==
        selectedEEComponentReplaceDetails?.assembly_number ||
        supplierName?.trim() !==
        selectedEEComponentReplaceDetails?.supplier_name ||
        // version?.trim() !== selectedEEComponentReplaceDetails?.version ||
        description?.trim() !==
        selectedEEComponentReplaceDetails?.description ||
        summary?.trim() !== selectedEEComponentReplaceDetails?.summary ||
        haveVehicleSystemsChanged() ||
        normalizeOptionalId(vcId) !== getExistingVehicleComponentId() ||
        selectedASIL !==
        selectedEEComponentReplaceDetails?.other_fields?.asil_class)
    );
  };


  const isReplaceEEComponentValid = () =>
    isReplaceEEComponentDetailsValid() && discp?.trim()?.length >= 3;

  const isReplaceEENextEnabled = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    const assemblyChanged =
      assemblyNo?.trim() !== selectedEEComponentReplaceDetails?.assembly_number &&
      regexMin3?.test(assemblyNo?.trim()) &&
      assemblyNo?.trim()?.length >= 3 &&
      !assemblyNoError;

    const supplierChanged =
      supplierName?.trim() !== selectedEEComponentReplaceDetails?.supplier_name &&
      supplierName?.length > 0;

    return assemblyChanged || supplierChanged;
  };

  const isUpdateDraftEEComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      assemblyNo?.trim() !== "" &&
      assemblyNo?.trim()?.length >= 3 &&
      !assemblyNoError &&
      regexMin3?.test(assemblyNo?.trim()) &&
      supplierName?.length > 0 &&
      version?.trim() !== "" &&
      version?.trim()?.length >= 3 &&
      !versionError &&
      regexMin3?.test(version?.trim()) &&
      description?.trim() !== "" &&
      description?.trim()?.length >= 3 &&
      !descriptionError &&
      // selectedVSIds?.length > 0 &&
      // vcId !== "" &&
      // vcId != null &&
      // !vcIdError &&
      selectedASIL?.length > 0 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3)
    );
  };
  const isUpdateAgainDraftEEComponentValid = () => {
    return (
      assemblyNo?.trim() !== "" ||
      supplierName?.length > 0 ||
      version?.trim() !== "" ||
      description?.trim() !== "" ||
      summary?.trim() !== "" ||
      selectedVSIds?.length > 0 ||
      Boolean(vcId) ||
      selectedASIL?.length > 0
    );

  };

  const handleCloseEEComponentReplace = () => {
    setOpenEEComponentReplaceDialog(false);

    clearReplaceEEComponentFields();

    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
  };

  const handleUpdateClick = () => {
    setShowConfirmation(true); // Show confirmation dialog when update is clicked
  };

  const handleReplaceDialogSuccess = () => {
    if (
      (title === "Replace EE Component" ||
        title === "Add Draft EE Component") &&
      replaceTabValue === 0
    ) {
      setReplaceTabValue(1);
      return;
    }

    if (title === "Replace EE Component") {
      handleUpdateClick();
    } else {
      handleUpdateDraftClickEE();
    }
  };

  const handleUpdateDraftClickEE = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(selectedProjectDetails?.is_zonal && {
        zone_position: selectedProjectDetails?.project_zones?.find(
          (zones) => zones?.zone_name === selectedZone?.zone_position_name,
        )?.position,
      }),
      ...(selectedVariantsMany?.length > 0
        ? { variant_ids: selectedVariantsMany }
        : []),
      save_as_draft: false,
      assembly_number: assemblyNo,
      supplier_name: supplierName,

      version: version?.trim(),
      ...(summary ? { summary: summary } : null),
      description: description,
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      ...(vcId ? { vc_id: Number(vcId) } : null),

      other_fields: {
        asil_class: selectedASIL,
      },

      current_time: moment().format(),
    };
    dispatch(
      HandleApiActions({
        api_name: "/VLC/ee_components/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_EE_COMPONENT_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentReplace();
      // getZoneEECountList(
      //   selectedProjectDetails?.project_id,
      //   selectedSingleVariantDetails?.variant_id
      // );
    });
  };

  const handleUpdateAgainDraftClickEE = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(selectedProjectDetails?.is_zonal && {
        zone_position: selectedProjectDetails?.project_zones?.find(
          (zones) => zones?.zone_name === selectedZone?.zone_position_name,
        )?.position,
      }),
      variant_ids: selectedVariantsMany,
      save_as_draft: true,
      assembly_number: assemblyNo,
      ...(supplierName ? { supplier_name: supplierName } : null),

      ...(version ? { version: version?.trim() } : null),
      ...(summary ? { summary: summary } : null),
      ...(description ? { description: description } : null),
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      ...(vcId ? { vc_id: Number(vcId) } : null),
      ...(selectedASIL?.length > 0
        ? {
          other_fields: {
            asil_class: selectedASIL,
          },
        }
        : null),
      current_time: moment().format(),
    };

    dispatch(
      HandleApiActions({
        api_name: "/VLC/ee_components/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_EE_COMPONENT_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentReplace();
      // getZoneEECountList(
      //   selectedProjectDetails?.project_id,
      //   selectedSingleVariantDetails?.variant_id
      // );
    });
  };

  const updateAttribute = async () => {
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const updatedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributes = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [];

    if (
      addedAttributes.length > 0 ||
      updatedAttributes.length > 0 ||
      deletedAttributes.length > 0
    ) {
      const attributeResp = await dispatch(
        HandleApiActions({
          ...labCar.updateEEAttribute,
          params: {
            entity_id: Number(selectedEEComponentReplaceDetails?.ee_id),
            new_attributes: addedAttributes,
            updated_attributes: updatedAttributes,
            deleted_attributes: deletedAttributes,
          },
          show_toast: false,
        }),
      );

      if (attributeResp?.error) {
        setDisableActionButton(false);
        // return;
      }
    }
  };

  const confirmUpdateAndRedirect = async ({ willImptIntrOnly = false }) => {
    setDisableActionButton(true); // Disable the action button to prevent multiple clicks
    if (
      selectedEEComponentReplaceDetails &&
      selectedEEComponentReplaceDetails?.ee_id
    ) {

      await updateAttribute();

      const reqObj = {
        current_time: new Date().toISOString(),
        variant_ids: selectedVariantsMany?.map(Number),
        project_id: Number(selectedEEComponentReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo?.trim(),
        ...(supplierName && { supplier_name: supplierName }),
        version: version?.trim(),
        ...(summary && { summary }),
        description: description,
        ...(selectedVSIds?.length > 0
          ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
          : null),
        ...(vcId ? { vc_id: Number(vcId) } : null),
        other_fields: {
          asil_class: selectedASIL,
        },
        will_it_impact: true,
        reason: discp,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        );
        if (!response.error) {
          handleCloseEEComponentReplace();
          willImptIntrOnly
            ? setActionType2DialogOpen(true)
            : setActionType1DialogOpen(true);
          if (willImptIntrOnly) {
            const resp = await dispatch(
              HandleApiActions({
                ...labCar.update_action_type,
                params: {
                  current_time: new Date().toISOString(),
                  project_id: Number(
                    selectedEEComponentReplaceDetails?.project_id,
                  ),
                  variant_id: Number(selectedSingleVariantDetails?.variant_id),
                  ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
                  is_dependency_linking: true,
                },
                show_toast: false,
              }),
            );
          }
          getEEComponentsList();
          getSWModulesList();
        }
      } catch (error) {
        console.error("Failed to replace", error);
      }
    }
    setDisableActionButton(false);
  };

  const handleCloseEEComponentReplaceDialog = () => {
    setOpenEEComponentReplaceDialog(false);
  };

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      tickets_type: 2,
    };

    try {
      // First API call
      const response = await dispatch(
        HandleApiActions({
          ...typeApprovalAPI.get_tickets,
          params: reqObj,
          show_toast: false,
        }),
      );
      handleCloseEEComponentReplaceDialog();
      setShowIntersectionImpactPrompt(false);

      // Extract and deduplicate standards
      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
              ?.map((ticket) => ticket?.ls_details?.standard_number)
              ?.filter((std) => Boolean(std)) // remove empty/undefined
            : [],
        ),
      ];

      if (standardsWithRuleSet?.length > 0) {
        // Second API call
        const standardWithRuleSet = await dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_engine_with_standards,
            params: { standard: standardsWithRuleSet },
            show_toast: false,
          }),
        );

        if (
          standardWithRuleSet?.data &&
          (Array.isArray(standardWithRuleSet.data)
            ? standardWithRuleSet?.data?.length > 0
            : Object.keys(standardWithRuleSet?.data)?.length > 0)
        ) {
          setShowRuleSetListDialog(true);
        } else {
          replaceChangesWithoutStandardAndClose();
        }

        return standardWithRuleSet.data || [];
      } else {
        updateChangesAndClose();
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch standard with ruleset", error);
      return false;
    }
  };
  const replaceComponentAndCheckForDecisionMatrix = async ({
    standardCode,
  } = {}) => {
    try {

      const reCreateTicketParams = {
        details: createTicketData,
        reason: discp,
      };

      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.re_create_ticket_v1,
          params: reCreateTicketParams,
          show_toast: true,
        }),
      );

      if (!res?.error && standardCode) {
        handleTicketCreationSuccess(standardCode);
      }
      return res;


    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleTicketCreationSuccess = (standardCode) => {
    setDisabledStandards((prev) => [...new Set([...prev, standardCode])]);
  };

  const loadDecisionMatrixContext = async () => {
    // 1. Fetch context based on component
    const standardsRes = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.decision_matrix_get_context,
        params: {
          component_type: "EE",
          component_id: selectedEEComponentDetails.ee_id,
          project_id: selectedProjectDetails.project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
        },
        show_toast: false,
      }),
    );
    setOpenEEComponentReplaceDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);

    const contextData = standardsRes?.data;

    const isValid = isDecisionMatrixContextValid(contextData);

    if (!isValid) {
      console.warn(
        "Invalid decision matrix context, cannot proceed:",
        contextData,
      );
      setDecisionMatrixContext(null);
      setShowDecisionMatrix(false);
      return;
    }

    setDecisionMatrixContext(standardsRes?.data);
    // setDiscp("");

    setShowDecisionMatrix(true);
  };
  const isDecisionMatrixContextValid = (data) => {
    if (!data?.standards || data.standards.length === 0) {
      return false;
    }

    return data.standards.some((standard) => {
      if (!standard.rule_sets || standard.rule_sets.length === 0) {
        return false;
      }

      return standard.rule_sets.some(
        (ruleSet) => ruleSet.workflows && ruleSet.workflows.length > 0,
      );
    });
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseEEComponentReplace();
  };

  const replaceChangesWithoutStandardAndClose = async () => {
    await updateAttribute();
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedVariantsMany,
      project_id: Number(selectedEEComponentReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
      assembly_number: assemblyNo?.trim(),
      ...(supplierName && { supplier_name: supplierName }),
      version: version?.trim(),
      ...(summary && { summary }),
      description: description,
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      ...(vcId ? { vc_id: Number(vcId) } : null),
      other_fields: {
        asil_class: selectedASIL,
      },
      will_it_impact: false,
      reason: discp,
      requires_homologation: false,
    };

    try {
      // Await the dispatch to ensure you're getting the response
      const response = await dispatch(
        HandleApiActions({
          ...labCar.replace_ee_component,
          params: reqObj,
          show_toast: true,
        }),
      );
      getEEComponentsList();
      getSWModulesList();

      if (response?.data?.snow_resp) {

      }
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const updateChangesAndClose = async () => {
    // Get requires_homologation value from the last node in the rule path
    const lastNode = rulePath[rulePath?.length - 1]?.node;
    const requiresHomologation = lastNode?.data?.requires_homologation || false;
    if (
      selectedEEComponentReplaceDetails &&
      selectedEEComponentReplaceDetails?.ee_id
    ) {
      const reqObj = {
        variant_ids: selectedVariantsMany?.map(Number),
        current_time: new Date().toISOString(),
        project_id: Number(selectedEEComponentReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo?.trim(),
        supplier_name: supplierName,
        version: version?.trim(),
        ...(summary && { summary }),
        description: description,
        ...(selectedVSIds?.length > 0
          ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
          : null),
        ...(vcId ? { vc_id: Number(vcId) } : null),
        other_fields: {
          asil_class: selectedASIL,
        },
        will_it_impact: false,
        requires_homologation: requiresHomologation,
        reason: discp,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        );
        getEEComponentsList();
        getSWModulesList();
        if (!response.error) {
          handleCloseEEComponentReplace();
        }
        if (response?.data?.snow_resp) {
          //       await loadDecisionMatrixContext();
          // setShowDecisionMatrix(true);
        }
      } catch (error) {
        console.error("Failed to replace", error);
      }
    }
  };

  const handleReplaceEEComponent = ({ }) => {
    if (
      selectedEEComponentReplaceDetails &&
      selectedEEComponentReplaceDetails?.ee_id
    ) {
      handleEEComponentReplace({
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo?.trim(),
        supplier_name: supplierName,
        version: version?.trim(),
        summary: summary,
        description: description,
        ...(selectedVSIds?.length > 0
          ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
          : null),
        ...(vcId ? { vc_id: Number(vcId) } : null),
        other_fields: {
          asil_class: selectedASIL,
        },
      });

      handleCloseEEComponentReplace();
    }
  };

  useEffect(() => {
    customConsole({ selectedVSIds, vsOptionsList });
  }, [selectedVSIds, vsOptionsList]);

  const handleActionType1DialogOpen = (ecu) => {
    setSelectedECU(ecu);
    setActionType1DialogOpen(true);
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };
  /**
   * Closes the action type 2 dialog.
   */
  /*******  8bbe441d-5db6-4801-843b-8073c92b7f62  *******/
  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleRedirectionToTypeApproval = () => {
    navigate(
      `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id}&zone=${selectedZone?.zone_position_name}&tab=0`,
    );
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  const handleExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_start,
          params: {
            workflowId: payload.workflowId,
            version: payload.version,
          },
          show_toast: false,
        }),
      );

      setExecutionId(res.executionId);

      return res;
    }

    if (mode === "COMPLETE") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_node_complete,
          params: {
            executionId: payload.executionId,
            nodeId: payload.nodeId,
            input: payload.input,
          },
          show_toast: false,
        }),
      );

      return res;
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleViewExecution = (executionId) => {
    // navigate / open drawer / open audit view
  };

  useEffect(() => {
    if (
      openEEComponentReplaceDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      const currentOptionValue =
        (variantOptionList || [])?.find((v) => String(v?.value) === String(id))
          ?.value ?? id;
      setSelectedVariantsMany((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr?.map(String).includes(String(currentOptionValue));
        if (hasId) return prevArr;
        return [currentOptionValue, ...prevArr];
      });
    }
  }, [
    openEEComponentReplaceDialog,
    selectedSingleVariantDetails,
    setSelectedVariantsMany,
  ]);

  //function to handel a get a list of tickets for a legal requirements
  const handelGetSimilerTickets = async ({ standard_name } = {}) => {
    try {
      const reCreateTicketParams = {
        execution_id: executionId,
        entity_type: "ee",
        entity_id: Number(selectedEEComponentDetails?.ee_id),
        ...(standard_name != null ? { standard_name } : {}),
      };

      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_similar_tickets,
          params: reCreateTicketParams,
          show_toast: true,
        }),
      );

      setSimilarTickets(res?.data || []);
      setCreateTicketData(res?.data || []);
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };
  const handelGetAiSuggestion = (ticket) => {
    setSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "EE Component",
    });
    setOpenAiPanel(true);
    setSelectedTicket(ticket);
  };

  const handelCloseDecisionMatrix = () => {
    setShowDecisionMatrix(false);
    setDecisionMatrixContext(null);
    setRulePath([]);
    setSimilarTickets([]);
    setSelectedEntity(null);
    setSelectedTicket(null);
    setCreateTicketData({});
    setDisabledStandards([]);
  };
  return (
    <Box>

      <CustomDialogBoxV2
        open={openEEComponentReplaceDialog}
        onClose={handleCloseEEComponentReplace}
        title={title}
        successButtonName={
          title === "Replace EE Component" || title === "Add Draft EE Component"
            ? replaceTabValue == 0
              ? "Next"
              : title === "Replace EE Component"
                ? "Replace"
                : "Add"
            : "Add"
        }
        rejectButtonName={
          title === "Replace EE Component"
            ? "Cancel"
            : replaceTabValue === 1
              ? "Save Draft"
              : null
        }
        onDisabled={
          title === "Replace EE Component"
            ? replaceTabValue == 0
              ? !isReplaceEENextEnabled()        // ← new function for Next
              : !isReplaceEEComponentValid()
            : replaceTabValue === 0
              ? !isUpdateAgainDraftEEComponentValid()
              : !isUpdateDraftEEComponentValid()
        }
        rejectDisabled={
          title === "Replace EE Component"
            ? false
            : !isUpdateAgainDraftEEComponentValid()
        }
        rejectButtonType={title === "Replace EE Component" ? "cancel" : "other"}
        onClickSuccess={handleReplaceDialogSuccess}
        // onClickSuccess={handleUpdateClick}
        onClickReject={
          title === "Replace EE Component"
            ? handleCloseEEComponentReplace
            : handleUpdateAgainDraftClickEE
        }
        actionButtonStyle={"three"}
        otherButtonName={
          (title === "Replace EE Component" ||
            title === "Add Draft EE Component") &&
            replaceTabValue === 1
            ? "Back"
            : null
        }
        onOtherButtonClick={() => setReplaceTabValue(0)}
        disabled={false}
        typeForThree={"cancel"}
        otherButtonPlacement={"left"}
      >
        <CustomTabsV2
          defaultTab={replaceTabValue}
          value={replaceTabValue}
          onTabChange={(newValue) => setReplaceTabValue(newValue)}
          tabs={[
            {
              name: "Details",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Assembly No"}
                    required={true}
                    type={"text"}
                    fldType={"textR"}
                    value={assemblyNo || " "}
                    setValue={(value) =>
                      value?.length <= 40 && setAssemblyNo(value)
                    }
                    maxLength={40}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Version"}
                    required={true}
                    type={"text"}
                    fldType={"textR"}
                    value={version}
                    setValue={(value) =>
                      value?.length <= 40 && setVersion(value)
                    }
                    maxLength={40}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Description"}
                    required={true}
                    type={"text"}
                    fldType={"text"}
                    value={description}
                    setValue={(value) =>
                      value?.length <= 500 && setDescription(value)
                    }
                    valueError={descriptionError}
                    setValueError={setDescriptionError}
                    maxLength={500}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Summary"}
                    type={"text"}
                    fldType={"text"}
                    value={summary}
                    setValue={(value) =>
                      value?.length <= 250 && setSummary(value)
                    }
                    valueError={summaryError}
                    setValueError={setSummaryError}
                    maxLength={250}
                  />
                  <CustomSelectV2
                    size={"small"}
                    required={true}
                    label={"Select Supplier Name"}
                    optionsList={supplierOptionsList}
                    selectedValue={supplierName || " "}
                    handleOnChange={(e) => setSupplierName(e.target.value)}
                  />

                  <SelectVehicleComponentForVehicleSystem
                    open={openEEComponentReplaceDialog}
                    projectId={selectedProjectDetails?.project_id}
                    variantId={selectedSingleVariantDetails?.variant_id}
                    isZonal={selectedProjectDetails?.is_zonal}
                    zonePosition={selectedZoneDetails?.position}
                    getSelectedVehicleSystem={handleSelectedVehicleSystem}
                    getSelectedVehicleComponent={handleSelectedVehicleComponent}
                    setVcIdError={setVcIdError}
                    vcId={
                      selectedEEComponentReplaceDetails
                        ?.vehicle_component_details[0]?.vc_id
                    }
                    vsId={
                      selectedEEComponentReplaceDetails
                        ?.vehicle_component_details[0]?.vs_id
                    }
                  // vcId={vcId}
                  // vsId={selectedVSIds[0]}
                  // disabled={true}
                  />
                  {/* <Box>
                    <MultiselectConnectorV2
                      required
                      multiple
                      size={"small"}
                      label={"Select Vehicle Systems"}
                      optionsList={vsOptionsList?.map((el) => ({
                        value: Number(el?.id),
                        label: el?.name,
                      }))}
                      selectedValues={selectedVSIds || " "}
                      setSelectedValues={setSelectedVSIds}
                      placeholder={false}
                    />
                  </Box> */}
                  <CustomSelectV2
                    size={"small"}
                    label={"Select ASIL Class"}
                    required={true}
                    optionsList={["A", "B", "C", "D"]?.map((el) => ({
                      value: el,
                      label: el,
                    }))}
                    selectedValue={selectedASIL || " "}
                    handleOnChange={(e) => setSelectedASIL(e.target.value)}
                  />
                  <MultiselectConnectorV2
                    multiple
                    selectedValues={selectedVariantsMany}
                    setSelectedValues={setSelectedVariantsMany}
                    label={"Select Variants"}
                    optionsList={(variantOptionList || [])?.filter(
                      (variants) => {
                        const currentId =
                          selectedSingleVariantDetails?.variant_id;
                        const alreadyAdded = (
                          selectedEEComponentReplaceDetails?.variant_ids || []
                        )
                          ?.map((v) => String(v))
                          ?.includes(String(variants?.value));
                        return (
                          alreadyAdded ||
                          String(variants?.value) === String(currentId)
                        );
                      },
                    )}
                    immutableValues={(() => {
                      const opt = (variantOptionList || [])?.find(
                        (v) =>
                          String(v?.value) ===
                          String(selectedSingleVariantDetails?.variant_id),
                      );
                      return opt && opt.value != null ? [opt.value] : [];
                    })()}
                    placeholder={false}
                  />
                  {/* {title === "Replace EE Component" ? (
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Reason"}
                      required={true}
                      type={"text"}
                      fldType={"textO"}
                      value={discp}
                      setValue={(value) =>
                        value?.length <= 100 && setDiscp(value)
                      }
                      valueError={discpError}
                      maxLength={100}
                    />
                  ) : null} */}
                </Stack>
              ),
            },
            {
              name: "Attribute",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <AttributesForm
                    type={"EDIT"}
                    attributeFromApi={
                      selectedEEComponentReplaceDetails?.attributes || []
                    }
                    getFormData={handleEEAttributeEdit}
                    reason={discp}
                    setReason={setDiscp}
                    reasonError={discpError}
                    showReason={title == "Replace EE Component"}
                  />
                </Stack>
              ),
            },
          ]}
        />

        <CustomDialogBoxV2
          open={showConfirmation}
          onClose={() => {
            setShowConfirmation(false);
          }}
          title="Action"
          dialogActions={
            <Box
              sx={{
                display: "flex",
                width: "350px",
                justifyContent: "space-between",
                height: "50px",
              }}
            >
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="No"
                handleOnClick={() => {
                  setShowConfirmation(false);

                  setTimeout(() => {
                    setShowIntersectionImpactPrompt(true);
                  }, 200);
                }}
              />
              <CustomButtonV2
                size="small"
                type="submit"
                btnName="Yes"
                handleOnClick={confirmUpdateAndRedirect}
                disabled={disableActionButton}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Will it impact the existing dependencies?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
        <CustomDialogBoxV2
          open={showIntersectionImpactPrompt}
          onClose={() => {
            setShowIntersectionImpactPrompt(false);
          }}
          title="Action"
          dialogActions={
            <Box
              sx={{
                display: "flex",
                width: "350px",
                justifyContent: "space-between",
                height: "50px",
              }}
            >
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="No"
                handleOnClick={() => {
                  loadDecisionMatrixContext();
                  replaceChangesWithoutStandardAndClose();
                }}
              />
              <CustomButtonV2
                size="small"
                type="submit"
                btnName="Yes"
                handleOnClick={() =>
                  confirmUpdateAndRedirect({ willImptIntrOnly: true })
                }
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Will it impact the existing intersection?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
      </CustomDialogBoxV2>
      {/* Action Dialog Type 1 for EE Component Dependency Linking */}
      <Box>
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          // onClose={handelCloseDecisionMatrix}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>EE Component dependency linking is pending!</Typography>
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={handleRedirectionToTypeApproval} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              EE Component intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        {/* Handle Ticket Page Dialog */}
        <CustomDialogBoxV2
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          title={"Tickets Recreated"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CustomButtonV2
                btnName="Close"
                handleOnClick={handleTicketsDialogClose}
                type="cancel"
              />
              <CustomButtonV2
                btnName="View"
                handleOnClick={() => {
                  navigate(`/dashboard/v2/type-approval/tickets`);
                  setTicketsDialogOpen(false);
                }}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                // ...componentStyle.font.text,
                fontSize: "15px",
              }}
            >
              Successfully created the tickets for the intersections!
            </Typography>
          </Box>
        </CustomDialogBoxV2>
        <Dialog
          open={showDecisionMatrix}
          onClose={handelCloseDecisionMatrix}
          fullScreen
          PaperProps={{
            sx: {
              width: "100vw",
              height: "100vh",
              margin: 0,
              borderRadius: 0,
            },
          }}
        >
          {/* Deloitte AppBar */}
          <AppBar
            position="sticky"
            sx={{
              backgroundColor: "#26890D",
            }}
          >
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Decision Matrix
              </Typography>
              <IconButton color="inherit" onClick={handelCloseDecisionMatrix}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* Body */}
          <Box
            sx={{
              flex: 1,
              height: "calc(100vh - 64px)",
              overflow: "auto",
              p: 2,
            }}
          >
            {showDecisionMatrix && (
              <DecisionMatrixPanel
                context={decisionMatrixContext}
                key={showDecisionMatrix ? "open" : "closed"}
                onExecuteWorkflow={handleExecuteWorkflow}
                onViewExecution={handleViewExecution}
                onClose={handelCloseDecisionMatrix}
                onDecisionMatrixAction={
                  replaceComponentAndCheckForDecisionMatrix
                }
                handelGetSimilerTickets={handelGetSimilerTickets}
                similarTickets={similarTickets}
                setSimilarTickets={setSimilarTickets}
                entityDetails={selectedEntity}
                handelGetAiSuggestion={handelGetAiSuggestion}
                selectedTicket={selectedTicket}
                setSelectedTicket={setSelectedTicket}
                openAiPanel={openAiPanel}
                setOpenAiPanel={setOpenAiPanel}
                setCreateTicketData={setCreateTicketData}
                disabledStandards={disabledStandards}
              />
            )}
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
}

VlcEEComponents.propTypes = {
  // API / Data fetch
  getZoneEECountList: PropTypes.func,
  getEEComponentsList: PropTypes.func,

  // Lists
  variantOptionList: PropTypes.array,
  eEComponentsList: PropTypes.array,
  swmListToFilter: PropTypes.array,
  filteredSWMList: PropTypes.array,
  sortedSWMList: PropTypes.array,

  // Strings / UI
  heading: PropTypes.string,
  searchFieldValue: PropTypes.string,
  selectedSearchField: PropTypes.string,
  swmNameFilter: PropTypes.string,
  swmVersionFilter: PropTypes.string,
  swmNameFilterSearch: PropTypes.string,
  swmVersionFilterSearch: PropTypes.string,
  swmSortField: PropTypes.string,
  swmSortOrder: PropTypes.string,

  // Objects
  selectedSingleVariantDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedZoneDetails: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,

  // Functions
  handleEEComponentAdd: PropTypes.func,
  handleEEComponentUpdate: PropTypes.func,
  handleEEComponentReplace: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  setSearchFieldValue: PropTypes.func,
  setSelectedSearchField: PropTypes.func,
  setPage: PropTypes.func,
  setRowsPerPage: PropTypes.func,
  setHasMore: PropTypes.func,
  setSwmPage: PropTypes.func,
  setSwmRowsPerPage: PropTypes.func,
  setSwmHasMore: PropTypes.func,
  setSWMNameFilter: PropTypes.func,
  setSWMVersionFilter: PropTypes.func,
  setSWMNameFilterSearch: PropTypes.func,
  setSWMVersionFilterSearch: PropTypes.func,
  setSWMSortField: PropTypes.func,
  setSWMSortOrder: PropTypes.func,
  setSelectedEEComponentDetails: PropTypes.func,
  setTotalEECount: PropTypes.func,
  setTotalSWMCount: PropTypes.func,

  // Pagination
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  hasMore: PropTypes.bool,

  swmPage: PropTypes.number,
  swmRowsPerPage: PropTypes.number,
  swmHasMore: PropTypes.bool,

  // Counts
  totalEECount: PropTypes.number,
  totalSWMCount: PropTypes.number,
};

VlcEEComponentUpdateDialog.propTypes = {
  // Lists
  variantOptionList: PropTypes.array,

  // Dialog control
  openEEComponentUpdateDialog: PropTypes.bool,
  setOpenEEComponentUpdateDialog: PropTypes.func,

  // Handlers
  handleEEComponentUpdate: PropTypes.func,
  setSelectedEEComponentUpdateDetails: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  getEEComponentsList: PropTypes.func,
  getSWModulesList: PropTypes.func,
  setIsVersionImpactFlow: PropTypes.func,

  // Selected objects
  selectedEEComponentUpdateDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  isVersionImpactFlow: PropTypes.bool,
  resumeAttributeImpactFlowTrigger: PropTypes.number,
  resumeVersionImpactFlowTrigger: PropTypes.number,

  // Variant details (nested access safe)
  selectedSingleVariantDetails: PropTypes.object,
};

VlcEEComponentReplaceDialog.propTypes = {
  // Title
  title: PropTypes.string,

  // Dialog control
  openEEComponentReplaceDialog: PropTypes.bool,
  setOpenEEComponentReplaceDialog: PropTypes.func,

  // API / Data fetch
  getZoneEECountList: PropTypes.func,
  getEEComponentsList: PropTypes.func,
  getSWModulesList: PropTypes.func,

  // Handlers
  handleEEComponentReplace: PropTypes.func,
  setSelectedEEComponentReplaceDetails: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,

  // Lists
  vsOptionsList: PropTypes.array,
  variantOptionList: PropTypes.array,
  supplierOptionsList: PropTypes.array,

  // Selected objects
  selectedEEComponentReplaceDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedZoneDetails: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  selectedSingleVariantDetails: PropTypes.object,
};
